package org.gymly.model

import org.gymly.util.GymlyCurrency
import org.gymly.util.formatMoney
import org.gymly.util.gymlyDateIsoFromInstant

/**
 * `label` es el nombre estable del módulo para auditoría y logs; **no** se usa en la UI.
 * Para mostrar el nombre traducido usa `strings().nav.route(route)`.
 */
enum class AdminRoute(val label: String, val group: AdminNavGroup?) {
    Dashboard("Panel", null),
    BulkSignup("Alta masiva", null),
    Reports("Reportes", null),
    Clients("Clientes", AdminNavGroup.People),
    Workers("Trabajadores", AdminNavGroup.People),
    Services("Servicios", AdminNavGroup.Sales),
    MembershipPlans("Planes de membresía", AdminNavGroup.Sales),
    MembershipOrders("Órdenes de membresía", AdminNavGroup.Sales),
    Memberships("Membresías", AdminNavGroup.Sales),
    ClientAccounts("Monedero digital", AdminNavGroup.Sales),
    Orders("Órdenes", null),
    Payments("Pagos", null),
    Marketing("Marketing", AdminNavGroup.Sales),
    Products("Productos", AdminNavGroup.Store),
    StoreSales("Ventas", AdminNavGroup.Store),
    Schedules("Horarios semanales", AdminNavGroup.Agenda),
    Bookings("Asistencia y sesiones", AdminNavGroup.Agenda),
    Profile("Perfil", AdminNavGroup.System),
    Settings("Configuración", AdminNavGroup.System),
    TermsMonitoring("Monitoreo de términos", AdminNavGroup.System),
}

/** Igual que [AdminRoute.label]: nombre estable, no para UI. Usa `strings().nav.group(group)`. */
enum class AdminNavGroup(val label: String) {
    People("Personal"),
    Sales("Catálogo y ventas"),
    Store("Tienda"),
    Agenda("Horario y reservas"),
    System("Sistema"),
}

data class AdminUser(
    val id: String,
    val displayName: String,
    val email: String?,
    val role: String,
    val photoUrl: String? = null,
    val preferredLocale: String? = null,
    /** Bumps when photo bytes change so the top bar can refetch / refresh. */
    val photoRevision: Int = 0,
    val membershipOverride: Boolean = false,
    val permissions: Set<AdminSettingsPermissionModule> = emptySet(),
    val permissionsKnown: Boolean = false,
)

data class AdminMe(
    val id: String,
    val displayName: String,
    val email: String?,
    val role: String,
    val roleLabel: String,
    val active: Boolean,
    val photoUrl: String?,
    val preferredLocale: String?,
    val branchId: String?,
    val branchName: String?,
    val membershipOverride: Boolean = false,
    val phone: String? = null,
    val docId: String? = null,
    val address: String? = null,
    val sex: AdminPersonSex? = null,
    val createdAt: String? = null,
    val permissions: Set<AdminSettingsPermissionModule> = emptySet(),
    val workSchedule: List<AdminWorkDaySchedule> = emptyList(),
) {
    val firstName: String get() = displayName.trim().substringBefore(' ').ifBlank { displayName }
    val lastName: String get() = displayName.trim().substringAfter(' ', "")
}

data class AdminMeUpsert(
    val displayName: String,
    val email: String?,
    val preferredLocale: String? = null,
    val phone: String? = null,
    val docId: String? = null,
    val address: String? = null,
    val sex: AdminPersonSex? = null,
)

data class AdminKpi(val label: String, val value: String, val subtitle: String? = null)

data class AdminListRow(val id: String, val title: String, val subtitle: String, val badge: String? = null)

data class AdminDashboardSummary(
    val activeClients: Long,
    val trainers: Long,
    val onTimePaymentPercent: Int,
    val scheduleSlots: Long,
    val bookingsToday: Long,
    val bookingsThisWeek: Long,
)

/** Métricas públicas del login (misma fuente que el dashboard admin). */
data class PublicMarketingStats(
    val activeClients: Long,
    val trainers: Long,
    val onTimePaymentPercent: Int,
)

data class AdminTrainer(
    val id: String,
    val displayName: String,
    val email: String?,
    val role: String,
)

enum class AdminWeekday(val iso: Int, val labelEn: String, val labelEs: String) {
    MONDAY(1, "Monday", "Lunes"),
    TUESDAY(2, "Tuesday", "Martes"),
    WEDNESDAY(3, "Wednesday", "Miércoles"),
    THURSDAY(4, "Thursday", "Jueves"),
    FRIDAY(5, "Friday", "Viernes"),
    SATURDAY(6, "Saturday", "Sábado"),
    SUNDAY(7, "Sunday", "Domingo");

    val shortEn: String get() = labelEn.take(3)
    val shortEs: String get() = when (this) {
        MONDAY -> "Lun"
        TUESDAY -> "Mar"
        WEDNESDAY -> "Mié"
        THURSDAY -> "Jue"
        FRIDAY -> "Vie"
        SATURDAY -> "Sáb"
        SUNDAY -> "Dom"
    }

    /** Etiqueta corta con fecha para cabecera del grid (ej. «Mié · 27 ago»). */
    fun gridHeaderLabel(week: org.gymly.util.WeekRange?): String =
        if (week == null) shortEs else "$shortEs · ${dateTag(week)}"

    fun dateTag(week: org.gymly.util.WeekRange): String {
        val iso = week.dateForWeekday(iso).take(10)
        val parts = iso.split("-")
        val dayNum = parts.getOrNull(2)?.toIntOrNull() ?: return ""
        val month = parts.getOrNull(1)?.toIntOrNull()?.let { MONTH_ABBR.getOrNull(it) } ?: ""
        return if (month.isEmpty()) dayNum.toString() else "$dayNum $month"
    }

    companion object {
        /** Lunes a viernes — columnas del grid semanal de horarios. */
        val WORK_WEEK: List<AdminWeekday> = listOf(MONDAY, TUESDAY, WEDNESDAY, THURSDAY, FRIDAY)

        private val MONTH_ABBR = listOf(
            "", "ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic",
        )

        fun fromIso(value: Int): AdminWeekday =
            entries.firstOrNull { it.iso == value } ?: MONDAY

        fun fromIsoOrNull(value: Int): AdminWeekday? =
            entries.firstOrNull { it.iso == value }
    }
}

data class AdminScheduleSlot(
    val id: String,
    val weekday: Int,
    val startTime: String,
    val endTime: String,
    val title: String,
    val capacity: Int = 12,
    val kind: AdminScheduleKind = AdminScheduleKind.BOOKABLE_CLASS,
    val trainerId: String? = null,
    val trainerName: String? = null,
    val serviceId: String? = null,
    val serviceName: String? = null,
    val serviceType: AdminServiceType? = null,
    val shift: String? = null,
    val shiftLabel: String? = null,
    val weekStart: String? = null,
    val createdAt: String? = null,
    /** Inasistencia del entrenador (falta al trabajo). */
    val trainerAbsent: Boolean = false,
) {
    fun weekdayLabel(): String = AdminWeekday.fromIso(weekday).labelEs

    fun timeRangeLabel(): String {
        val start = startTime.take(5)
        val end = endTime.take(5)
        return "$start–$end"
    }

    fun slotLabel(): String = "${weekdayLabel()} · ${timeRangeLabel()}"

    fun kindLabelEs(): String = kind.labelEs

    fun shiftDisplay(): String = shiftLabel ?: when (shift?.uppercase()) {
        "DAY" -> "Diurno"
        "NIGHT" -> "Nocturno"
        "MIXED" -> "Mixto"
        else -> "—"
    }
}

enum class AdminScheduleKind(val labelEs: String) {
    BOOKABLE_CLASS("Clase (reservable)"),
    OPEN_FLOOR("Piso / entrenador"),
    ;

    companion object {
        fun fromApi(raw: String?): AdminScheduleKind =
            entries.firstOrNull { it.name.equals(raw?.trim(), ignoreCase = true) } ?: BOOKABLE_CLASS
    }
}

data class AdminScheduleUpsert(
    val weekday: Int,
    val startTime: String,
    val endTime: String,
    val title: String,
    val capacity: Int,
    val kind: AdminScheduleKind = AdminScheduleKind.BOOKABLE_CLASS,
    val trainerId: String?,
    val serviceId: String? = null,
    val weekStart: String? = null,
)

enum class AdminVisitSource {
    ACCESS,
    MANUAL,
    BOOKING_ATTENDED,
    ;

    fun labelEs(): String = when (this) {
        ACCESS -> "Acceso (huella/rostro)"
        MANUAL -> "Manual"
        BOOKING_ATTENDED -> "Clase asistida"
    }

    companion object {
        fun fromApi(raw: String?): AdminVisitSource =
            entries.firstOrNull { it.name.equals(raw?.trim(), ignoreCase = true) } ?: MANUAL
    }
}

data class AdminVisitItem(
    val id: String,
    val clientId: String? = null,
    val staffId: String? = null,
    val clientName: String? = null,
    val staffName: String? = null,
    val visitDate: String,
    val occurredAt: String,
    val source: AdminVisitSource,
    val scheduleId: String? = null,
    val scheduleTitle: String? = null,
    val trainerId: String? = null,
    val trainerName: String? = null,
    val bookingId: String? = null,
    val notes: String? = null,
    val createdAt: String? = null,
) {
    val isStaff: Boolean get() = !staffId.isNullOrBlank()
    val subjectName: String
        get() = when {
            isStaff -> staffName?.takeIf { it.isNotBlank() } ?: "—"
            else -> clientName?.takeIf { it.isNotBlank() } ?: "—"
        }
}

data class AdminVisitUpsert(
    val clientId: String? = null,
    val staffId: String? = null,
    val scheduleId: String? = null,
    val trainerId: String? = null,
    val occurredAt: String? = null,
    val notes: String? = null,
)

enum class AdminBookingStatus {
    CONFIRMED,
    ATTENDED,
    NO_SHOW,
    CANCELLED,
    ;

    companion object {
        fun fromApi(value: String): AdminBookingStatus =
            entries.firstOrNull { it.name.equals(value, ignoreCase = true) } ?: CONFIRMED
    }

    fun labelEs(): String = when (this) {
        CONFIRMED -> "Reservada"
        ATTENDED -> "Asistida"
        NO_SHOW -> "Cancelada"
        CANCELLED -> "Cancelada"
    }

    fun isReserved(): Boolean = this == CONFIRMED

    fun isCancelledBucket(): Boolean = this == CANCELLED || this == NO_SHOW

    fun dotColorArgb(): Long = when (this) {
        CONFIRMED -> 0xFF3B4CB8
        ATTENDED -> 0xFF2E7D32
        NO_SHOW, CANCELLED -> 0xFFE57373
    }

    fun badgeBgArgb(): Long = when (this) {
        CONFIRMED -> 0xFFE8EEFF
        ATTENDED -> 0xFFE8F5E9
        NO_SHOW, CANCELLED -> 0xFFFFEBEE
    }

    fun badgeFgArgb(): Long = when (this) {
        CONFIRMED -> 0xFF3B4CB8
        ATTENDED -> 0xFF2E7D32
        NO_SHOW, CANCELLED -> 0xFFC62828
    }
}

data class AdminBookingItem(
    val id: String,
    val clientId: String,
    val clientName: String,
    val scheduleId: String,
    val title: String,
    val sessionDate: String,
    val weekday: Int,
    val startTime: String,
    val endTime: String,
    val status: AdminBookingStatus,
    val notes: String? = null,
    val createdAt: String? = null,
) {
    fun franjaLabel(): String {
        val day = AdminWeekday.fromIso(weekday).shortEn
        val time = startTime.take(5)
        return "$day $time — $title"
    }
}

data class AdminBookingUpsert(
    val clientId: String,
    val scheduleId: String,
    val sessionDate: String? = null,
    val notes: String? = null,
)

data class AdminSearchHit(
    val id: String,
    val type: String,
    val title: String,
    val subtitle: String? = null,
)

data class AdminNotificationItem(
    val id: String,
    val title: String,
    val body: String? = null,
    val read: Boolean,
    val createdAt: String? = null,
    val kind: String? = null,
    val refType: String? = null,
    val refId: String? = null,
) {
    fun isApprovalNeeded(): Boolean = kind.equals("APPROVAL_NEEDED", ignoreCase = true)
    fun isApprovalResolved(): Boolean = kind.equals("APPROVAL_RESOLVED", ignoreCase = true)
    fun isApprovalRelated(): Boolean = isApprovalNeeded() || isApprovalResolved()
}

data class AdminNotificationFeed(
    val unreadCount: Long,
    val items: List<AdminNotificationItem>,
)

enum class AdminClientStatus {
    ACTIVE,
    INACTIVE,
    ;

    companion object {
        fun fromApi(value: String): AdminClientStatus =
            entries.firstOrNull { it.name.equals(value, ignoreCase = true) } ?: ACTIVE
    }

    fun labelEs(): String = when (this) {
        ACTIVE -> "Activo"
        INACTIVE -> "Inactivo"
    }
}

data class AdminBranch(
    val id: String,
    val name: String,
    val address: String? = null,
    val ownerId: String? = null,
    val ownerName: String? = null,
    val hasLogo: Boolean = false,
    val hasBanner: Boolean = false,
    val logoUrl: String? = null,
    val bannerUrl: String? = null,
    val bannerThumbUrl: String? = null,
    val createdAt: String? = null,
) {
    fun initials(): String =
        name.split(" ")
            .mapNotNull { it.firstOrNull()?.uppercaseChar() }
            .take(2)
            .joinToString("")
            .ifBlank { "S" }
}

data class AdminBranchUpsert(
    val name: String,
    val address: String? = null,
    val ownerName: String? = null,
    val ownerId: String? = null,
    val logo: PickedImagePayload? = null,
    val banner: PickedImagePayload? = null,
)

data class AdminCompanyRepresentative(
    val id: String? = null,
    val name: String,
    val title: String? = null,
    val docId: String? = null,
    val staffId: String? = null,
    val staffName: String? = null,
    val sortOrder: Int = 0,
)

data class AdminCompany(
    val id: String,
    val name: String,
    val address: String? = null,
    val taxId: String? = null,
    val hasLogo: Boolean = false,
    val hasBanner: Boolean = false,
    val logoUrl: String? = null,
    val bannerUrl: String? = null,
    val bannerThumbUrl: String? = null,
    val representatives: List<AdminCompanyRepresentative> = emptyList(),
    val createdAt: String? = null,
    val brandColor: String? = null,
    /** Zona horaria operativa fija (Venezuela). */
    val timezone: String = "America/Caracas",
    /** Horario de apertura de la instalación (días + bloques). */
    val operatingHours: List<AdminWorkDaySchedule> = emptyList(),
    /** Estadísticas del pie del login. */
    val loginShowClients: Boolean = true,
    val loginShowTrainers: Boolean = true,
    val loginShowOnTime: Boolean = true,
    /** Monitoreo de términos del personal (Empresa). */
    val termsMonitoringEnabled: Boolean = false,
    /** La licencia incluye portal o app: solo entonces se ofrece la casilla. */
    val termsMonitoringAvailable: Boolean = false,
    val termsNoticeCooldownDays: Int = 3,
) {
    fun initials(): String =
        name.split(" ")
            .mapNotNull { it.firstOrNull()?.uppercaseChar() }
            .take(2)
            .joinToString("")
            .ifBlank { "E" }
}

data class AdminCompanyRepresentativeUpsert(
    val name: String,
    val title: String? = null,
    val docId: String? = null,
    val staffId: String? = null,
)

data class AdminCompanyUpsert(
    val name: String,
    val address: String? = null,
    val taxId: String? = null,
    val brandColor: String? = null,
    /** Zona IANA del país (p. ej. America/Caracas para Venezuela). */
    val timezone: String = "America/Caracas",
    val representatives: List<AdminCompanyRepresentativeUpsert> = emptyList(),
    val operatingHours: List<AdminWorkDaySchedule> = emptyList(),
    val logo: PickedImagePayload? = null,
    val banner: PickedImagePayload? = null,
    val loginShowClients: Boolean = true,
    val loginShowTrainers: Boolean = true,
    val loginShowOnTime: Boolean = true,
    val termsMonitoringEnabled: Boolean? = null,
    val termsNoticeCooldownDays: Int? = null,
)

/** Branding público del login (sin auth). */
data class PublicCompanyBranding(
    val id: String,
    val name: String,
    val hasLogo: Boolean = false,
    val logoUrl: String? = null,
    val hasBanner: Boolean = false,
    val bannerUrl: String? = null,
    val brandColor: String? = null,
    val loginShowClients: Boolean = true,
    val loginShowTrainers: Boolean = true,
    val loginShowOnTime: Boolean = true,
)

/** Tipo en Configuración → Usuarios (directorio unificado). */
enum class AdminSettingsUserType {
    CLIENT,
    STAFF,
    TRAINER,
    ;

    companion object {
        fun fromApi(value: String): AdminSettingsUserType =
            entries.firstOrNull { it.name.equals(value, ignoreCase = true) } ?: CLIENT
    }

    fun labelEs(): String = when (this) {
        CLIENT -> "Cliente"
        STAFF -> "Personal"
        TRAINER -> "Entrenador"
    }

    fun apiPath(): String = name
}

enum class AdminSettingsUserStatus {
    ACTIVE,
    INACTIVE,
    ;

    companion object {
        fun fromApi(value: String): AdminSettingsUserStatus =
            entries.firstOrNull { it.name.equals(value, ignoreCase = true) } ?: ACTIVE
    }

    fun labelEs(): String = when (this) {
        ACTIVE -> "Activo"
        INACTIVE -> "Inactivo"
    }
}

data class AdminSettingsUser(
    val id: String,
    val type: AdminSettingsUserType,
    val displayName: String,
    val email: String?,
    val staffRole: AdminStaffRole?,
    val roleLabel: String?,
    val branchId: String?,
    val branchName: String?,
    val status: AdminSettingsUserStatus,
    val createdAt: String?,
) {
    fun roleDisplay(): String = when {
        type == AdminSettingsUserType.CLIENT -> "Cliente"
        staffRole != null -> staffRole.labelEs()
        !roleLabel.isNullOrBlank() -> roleLabel
        else -> "—"
    }
}

data class AdminSettingsUserUpsert(
    val type: AdminSettingsUserType,
    val displayName: String,
    val email: String? = null,
    val branchId: String? = null,
    val status: AdminSettingsUserStatus = AdminSettingsUserStatus.ACTIVE,
    val password: String? = null,
    val staffRole: AdminStaffRole? = null,
    /** Motivo al desactivar: de cliente o de trabajador según el tipo. */
    val inactivationReason: String? = null,
    val inactivationNote: String? = null,
)

enum class AdminStaffAuditEventType {
    LOGIN,
    LOGOUT,
    MODULE_VIEW,
    CLIENT_CREATED,
    CLIENT_UPDATED,
    CLIENT_DELETED,
    PAYMENT_COLLECTED,
    PAYMENT_VOIDED,
    MEMBERSHIP_ORDER_CREATED,
    MEMBERSHIP_ORDER_PAID,
    MEMBERSHIP_ORDER_COURTESY,
    STORE_SALE_CREATED,
    STORE_SALE_VOIDED,
    MEMBERSHIP_BLOCKED,
    MEMBERSHIP_RELEASED,
    MEMBERSHIP_OVERRIDE,
    APPROVAL_CREATED,
    APPROVAL_APPROVED,
    APPROVAL_REJECTED,
    ROLE_CREATED,
    ROLE_UPDATED,
    STAFF_USER_CREATED,
    STAFF_USER_UPDATED,
    PRODUCT_CREATED,
    PRODUCT_UPDATED,
    MEMBERSHIP_PLAN_CREATED,
    MEMBERSHIP_PLAN_UPDATED,
    MARKETING_PROMO_CREATED,
    MARKETING_PROMO_UPDATED,
    EXCHANGE_RATE_UPDATED,
    PAYMENT_METHOD_CREATED,
    PAYMENT_METHOD_UPDATED,
    LOGIN_FAILED,
    TERMS_NOTICE_QUEUED,
    TERMS_NOTICE_SENT,
    TERMS_NOTICE_FAILED,
    TERMS_ACCEPTED,
    TERMS_DECLINED,
    TERMS_REACTIVATED,
    TERMS_RESENT,
    TERMS_SUSPENDED,
    TERMS_NOTICE_PAUSED,
    TERMS_NOTICE_RESUMED,
    PORTAL_PAYMENT_UPDATED,
    PORTAL_MEMBERSHIP_ORDER_CREATED,
    PORTAL_STORE_ORDER_CREATED,
    PORTAL_BOOKING_CREATED,
    PORTAL_BOOKING_CANCELLED,
    PORTAL_PROFILE_UPDATED,
    PORTAL_PASSWORD_CHANGED,
    ;

    companion object {
        fun fromApi(value: String): AdminStaffAuditEventType =
            entries.firstOrNull { it.name.equals(value, ignoreCase = true) } ?: MODULE_VIEW
    }

    fun labelEs(): String = when (this) {
        LOGIN -> "Inicio de sesión"
        LOGOUT -> "Cierre de sesión"
        MODULE_VIEW -> "Módulo visitado"
        CLIENT_CREATED -> "Cliente creado"
        CLIENT_UPDATED -> "Cliente editado"
        CLIENT_DELETED -> "Cliente borrado"
        PAYMENT_COLLECTED -> "Cobro registrado"
        PAYMENT_VOIDED -> "Pago anulado"
        MEMBERSHIP_ORDER_CREATED -> "Orden de membresía creada"
        MEMBERSHIP_ORDER_PAID -> "Orden de membresía pagada"
        MEMBERSHIP_ORDER_COURTESY -> "Orden de membresía cortesía"
        STORE_SALE_CREATED -> "Venta tienda creada"
        STORE_SALE_VOIDED -> "Venta tienda anulada"
        MEMBERSHIP_BLOCKED -> "Membresía bloqueada"
        MEMBERSHIP_RELEASED -> "Membresía liberada"
        MEMBERSHIP_OVERRIDE -> "Override de membresía"
        APPROVAL_CREATED -> "Aprobación solicitada"
        APPROVAL_APPROVED -> "Aprobación concedida"
        APPROVAL_REJECTED -> "Aprobación rechazada"
        ROLE_CREATED -> "Rol creado"
        ROLE_UPDATED -> "Rol actualizado"
        STAFF_USER_CREATED -> "Usuario staff creado"
        STAFF_USER_UPDATED -> "Usuario staff editado"
        PRODUCT_CREATED -> "Producto creado"
        PRODUCT_UPDATED -> "Producto editado"
        MEMBERSHIP_PLAN_CREATED -> "Plan de membresía creado"
        MEMBERSHIP_PLAN_UPDATED -> "Plan de membresía editado"
        MARKETING_PROMO_CREATED -> "Promoción creada"
        MARKETING_PROMO_UPDATED -> "Promoción editada"
        EXCHANGE_RATE_UPDATED -> "Cotización actualizada"
        PAYMENT_METHOD_CREATED -> "Tipo de pago creado"
        PAYMENT_METHOD_UPDATED -> "Tipo de pago editado"
        LOGIN_FAILED -> "Login fallido"
        TERMS_NOTICE_QUEUED -> "Aviso de términos encolado"
        TERMS_NOTICE_SENT -> "Aviso de términos enviado"
        TERMS_NOTICE_FAILED -> "Aviso de términos fallido"
        TERMS_ACCEPTED -> "Socio aceptó los términos"
        TERMS_DECLINED -> "Socio rechazó los términos"
        TERMS_REACTIVATED -> "Socio reactivado"
        TERMS_RESENT -> "Términos reenviados a socio"
        TERMS_SUSPENDED -> "Socio suspendido"
        TERMS_NOTICE_PAUSED -> "Canal de avisos pausado"
        TERMS_NOTICE_RESUMED -> "Canal de avisos reanudado"
        PORTAL_PAYMENT_UPDATED -> "Socio (portal): comprobante de pago"
        PORTAL_MEMBERSHIP_ORDER_CREATED -> "Socio (portal): pago de membresía"
        PORTAL_STORE_ORDER_CREATED -> "Socio (portal): compra en tienda"
        PORTAL_BOOKING_CREATED -> "Socio (portal): reserva"
        PORTAL_BOOKING_CANCELLED -> "Socio (portal): reserva cancelada"
        PORTAL_PROFILE_UPDATED -> "Socio (portal): datos personales"
        PORTAL_PASSWORD_CHANGED -> "Socio (portal): cambio de contraseña"
    }

    fun filterGroup(): AdminStaffAuditFilterGroup = when (this) {
        LOGIN, LOGOUT, MODULE_VIEW, LOGIN_FAILED -> AdminStaffAuditFilterGroup.ACCESS
        CLIENT_CREATED, CLIENT_UPDATED, CLIENT_DELETED -> AdminStaffAuditFilterGroup.CLIENTS
        PAYMENT_COLLECTED, PAYMENT_VOIDED, STORE_SALE_CREATED, STORE_SALE_VOIDED,
        PRODUCT_CREATED, PRODUCT_UPDATED -> AdminStaffAuditFilterGroup.CASH
        MEMBERSHIP_ORDER_CREATED, MEMBERSHIP_ORDER_PAID, MEMBERSHIP_ORDER_COURTESY,
        MEMBERSHIP_BLOCKED, MEMBERSHIP_RELEASED, MEMBERSHIP_OVERRIDE,
        MEMBERSHIP_PLAN_CREATED, MEMBERSHIP_PLAN_UPDATED -> AdminStaffAuditFilterGroup.MEMBERSHIPS
        APPROVAL_CREATED, APPROVAL_APPROVED, APPROVAL_REJECTED -> AdminStaffAuditFilterGroup.APPROVALS
        ROLE_CREATED, ROLE_UPDATED, STAFF_USER_CREATED, STAFF_USER_UPDATED,
        EXCHANGE_RATE_UPDATED, PAYMENT_METHOD_CREATED, PAYMENT_METHOD_UPDATED -> AdminStaffAuditFilterGroup.CONFIG
        MARKETING_PROMO_CREATED, MARKETING_PROMO_UPDATED -> AdminStaffAuditFilterGroup.MARKETING
        TERMS_NOTICE_QUEUED, TERMS_NOTICE_SENT, TERMS_NOTICE_FAILED, TERMS_ACCEPTED, TERMS_DECLINED,
        TERMS_REACTIVATED, TERMS_RESENT, TERMS_SUSPENDED, TERMS_NOTICE_PAUSED, TERMS_NOTICE_RESUMED ->
            AdminStaffAuditFilterGroup.TERMS
        PORTAL_PAYMENT_UPDATED, PORTAL_STORE_ORDER_CREATED -> AdminStaffAuditFilterGroup.CASH
        PORTAL_MEMBERSHIP_ORDER_CREATED -> AdminStaffAuditFilterGroup.MEMBERSHIPS
        PORTAL_BOOKING_CREATED, PORTAL_BOOKING_CANCELLED, PORTAL_PROFILE_UPDATED, PORTAL_PASSWORD_CHANGED ->
            AdminStaffAuditFilterGroup.CLIENTS
    }
}

enum class AdminStaffAuditFilterGroup(val labelEs: String) {
    ACCESS("Accesos"),
    CLIENTS("Clientes"),
    CASH("Caja"),
    MEMBERSHIPS("Membresías"),
    CONFIG("Configuración"),
    APPROVALS("Aprobaciones"),
    MARKETING("Marketing"),
    TERMS("Términos de socios"),
}

data class AdminStaffAuditEvent(
    val id: String,
    val staffId: String,
    val staffName: String,
    val staffEmail: String?,
    val eventType: AdminStaffAuditEventType,
    val eventLabel: String,
    val moduleCode: String?,
    val moduleLabel: String?,
    val detail: String?,
    val sessionId: String?,
    val durationSeconds: Long?,
    val entityType: String? = null,
    val entityId: String? = null,
    val occurredAt: String,
) {
    fun displayDetail(): String {
        val raw = detail?.trim().orEmpty()
        if (raw.isNotEmpty() && !raw.equals("Tiempo en módulo", ignoreCase = true)) {
            return raw
        }
        return when (eventType) {
            AdminStaffAuditEventType.MODULE_VIEW ->
                "Permanencia en ${moduleLabel?.takeIf { it.isNotBlank() } ?: "módulo"}"
            AdminStaffAuditEventType.LOGIN -> "Acceso al panel admin"
            AdminStaffAuditEventType.LOGOUT -> "Cierre de sesión"
            AdminStaffAuditEventType.LOGIN_FAILED -> "Intento de acceso fallido"
            else -> "—"
        }
    }

    fun displayDuration(): String {
        if (eventType != AdminStaffAuditEventType.MODULE_VIEW) return "—"
        val seconds = durationSeconds ?: return "—"
        if (seconds <= 0) return "—"
        val mins = seconds / 60
        val hrs = mins / 60
        val remMin = mins % 60
        val remSec = seconds % 60
        return when {
            hrs > 0 -> "${hrs}h ${remMin}m"
            mins > 0 -> "${mins}m ${remSec}s"
            else -> "${seconds}s"
        }
    }

    fun entityLabelEs(): String? = when (entityType?.uppercase()) {
        "CLIENT" -> "Cliente"
        "PAYMENT" -> "Pago"
        "MEMBERSHIP_ORDER" -> "Orden de membresía"
        "STORE_SALE" -> "Venta"
        "PRODUCT" -> "Producto"
        "ROLE" -> "Rol"
        "MEMBERSHIP" -> "Membresía"
        "MEMBERSHIP_PLAN" -> "Plan"
        "MARKETING_PROMO" -> "Promoción"
        "STAFF_USER" -> "Usuario staff"
        else -> entityType
    }
}

data class AdminStaffAuditRecord(
    val eventType: AdminStaffAuditEventType,
    val moduleCode: String? = null,
    val moduleLabel: String? = null,
    val detail: String? = null,
    val sessionId: String? = null,
    val durationSeconds: Long? = null,
)

/** Permisos por módulo de negocio + acción (Ver / Usar / Borrar / Aprobar). */
enum class AdminSettingsPermissionModule {
    CLIENT_VIEW,
    CLIENT_OPERATE,
    CLIENT_DELETE,
    CLIENT_APPROVE,
    WORKER_VIEW,
    WORKER_OPERATE,
    WORKER_DELETE,
    WORKER_APPROVE,
    CASH_VIEW,
    CASH_OPERATE,
    CASH_DELETE,
    CASH_APPROVE,
    PRODUCT_VIEW,
    PRODUCT_OPERATE,
    PRODUCT_DELETE,
    PRODUCT_APPROVE,
    SALE_DELETE,
    SALE_APPROVE,
    MEMBERSHIPS_VIEW,
    MEMBERSHIPS_OPERATE,
    MEMBERSHIPS_DELETE,
    MEMBERSHIPS_APPROVE,
    MARKETING_VIEW,
    MARKETING_OPERATE,
    REPORTS_OPS_VIEW,
    REPORTS_MONEY_VIEW,
    CONFIG_OPS_VIEW,
    CONFIG_OPS_EDIT,
    CONFIG_BIZ_VIEW,
    CONFIG_BIZ_EDIT,
    CONFIG_SEC_VIEW,
    CONFIG_SEC_EDIT,
    TERMS_MONITOR_VIEW,
    TERMS_MONITOR_OPERATE,
    TERMS_MONITOR_APPROVE,
    ASISTENTE_OPERATE,
    SCHEDULE_VIEW,
    SCHEDULE_OPERATE,
    SCHEDULE_DELETE,
    SCHEDULE_APPROVE,
    ATTENDANCE_VIEW,
    ATTENDANCE_OPERATE,
    ATTENDANCE_DELETE,
    ATTENDANCE_APPROVE,
    // Permisos nuevos añadidos al final:
    SERVICE_DELETE,
    SERVICE_APPROVE,
    MEMBERSHIP_ORDER_VIEW,
    MEMBERSHIP_ORDER_OPERATE,
    MEMBERSHIP_ORDER_DELETE,
    MEMBERSHIP_ORDER_APPROVE,
    MARKETING_DELETE,
    MARKETING_APPROVE,
    ;

    fun labelEs(): String = when (this) {
        CLIENT_VIEW -> "Clientes · Ver"
        CLIENT_OPERATE -> "Clientes · Usar"
        CLIENT_DELETE -> "Clientes · Borrar"
        CLIENT_APPROVE -> "Clientes · Aprobar"
        WORKER_VIEW -> "Trabajadores · Ver"
        WORKER_OPERATE -> "Trabajadores · Usar"
        WORKER_DELETE -> "Trabajadores · Borrar"
        WORKER_APPROVE -> "Trabajadores · Aprobar"
        CASH_VIEW -> "Caja · Ver"
        CASH_OPERATE -> "Caja · Usar"
        CASH_DELETE -> "Caja · Anular"
        CASH_APPROVE -> "Caja · Aprobar"
        PRODUCT_VIEW -> "Productos · Ver"
        PRODUCT_OPERATE -> "Productos · Usar"
        PRODUCT_DELETE -> "Productos · Borrar"
        PRODUCT_APPROVE -> "Productos · Aprobar"
        SALE_DELETE -> "Ventas · Borrar"
        SALE_APPROVE -> "Ventas · Aprobar"
        MEMBERSHIPS_VIEW -> "Membresías · Ver"
        MEMBERSHIPS_OPERATE -> "Membresías · Usar"
        MEMBERSHIPS_DELETE -> "Membresías · Borrar"
        MEMBERSHIPS_APPROVE -> "Membresías · Aprobar"
        MARKETING_VIEW -> "Marketing · Ver"
        MARKETING_OPERATE -> "Marketing · Usar"
        REPORTS_OPS_VIEW -> "Reportes operativos"
        REPORTS_MONEY_VIEW -> "Reportes de dinero"
        CONFIG_OPS_VIEW -> "Config operativa · Ver"
        CONFIG_OPS_EDIT -> "Config operativa · Editar"
        CONFIG_BIZ_VIEW -> "Config negocio · Ver"
        CONFIG_BIZ_EDIT -> "Config negocio · Editar"
        CONFIG_SEC_VIEW -> "Config seguridad · Ver"
        CONFIG_SEC_EDIT -> "Config seguridad · Editar"
        TERMS_MONITOR_VIEW -> "Monitoreo de términos · Ver"
        TERMS_MONITOR_OPERATE -> "Monitoreo de términos · Usar"
        TERMS_MONITOR_APPROVE -> "Monitoreo de términos · Aprobar"
        ASISTENTE_OPERATE -> "Asistente IA · Usar"
        SCHEDULE_VIEW -> "Horarios · Ver"
        SCHEDULE_OPERATE -> "Horarios · Usar"
        SCHEDULE_DELETE -> "Horarios · Borrar"
        SCHEDULE_APPROVE -> "Horarios · Aprobar"
        ATTENDANCE_VIEW -> "Asistencia · Ver"
        ATTENDANCE_OPERATE -> "Asistencia · Usar"
        ATTENDANCE_DELETE -> "Asistencia · Borrar"
        ATTENDANCE_APPROVE -> "Asistencia · Aprobar"
        SERVICE_DELETE -> "Servicios · Borrar"
        SERVICE_APPROVE -> "Servicios · Aprobar"
        MEMBERSHIP_ORDER_VIEW -> "Órdenes · Ver"
        MEMBERSHIP_ORDER_OPERATE -> "Órdenes · Usar"
        MEMBERSHIP_ORDER_DELETE -> "Órdenes · Borrar"
        MEMBERSHIP_ORDER_APPROVE -> "Órdenes · Aprobar"
        MARKETING_DELETE -> "Marketing · Borrar"
        MARKETING_APPROVE -> "Marketing · Aprobar"
    }

    companion object {
        fun fromApi(value: String): AdminSettingsPermissionModule? =
            entries.firstOrNull { it.name.equals(value, ignoreCase = true) }
    }
}

enum class AdminPermissionCapabilityGroup {
    CLIENTS,
    WORKERS,
    CASH,
    PRODUCTS,
    SALES,
    MEMBERSHIPS,
    SERVICES,
    ORDERS,
    MARKETING,
    REPORTS_OPS,
    REPORTS_MONEY,
    CONFIG_OPS,
    CONFIG_BIZ,
    CONFIG_SEC,
    SCHEDULES,
    ATTENDANCE,
    ;

    fun labelEs(): String = when (this) {
        CLIENTS -> "Clientes"
        WORKERS -> "Trabajadores"
        CASH -> "Caja y cobros"
        PRODUCTS -> "Productos"
        SALES -> "Ventas"
        MEMBERSHIPS -> "Membresías"
        SERVICES -> "Servicios"
        ORDERS -> "Órdenes"
        MARKETING -> "Marketing"
        REPORTS_OPS -> "Reportes operativos"
        REPORTS_MONEY -> "Reportes de dinero"
        CONFIG_OPS -> "Config operativa"
        CONFIG_BIZ -> "Config negocio"
        CONFIG_SEC -> "Config seguridad"
        SCHEDULES -> "Horarios semanales"
        ATTENDANCE -> "Asistencia y sesiones"
    }

    fun actions(): List<AdminPermissionAction> = when (this) {
        REPORTS_OPS, REPORTS_MONEY -> listOf(AdminPermissionAction.VIEW)
        SERVICES -> listOf(AdminPermissionAction.VIEW, AdminPermissionAction.OPERATE, AdminPermissionAction.DELETE, AdminPermissionAction.APPROVE)
        ORDERS -> listOf(AdminPermissionAction.VIEW, AdminPermissionAction.OPERATE, AdminPermissionAction.DELETE, AdminPermissionAction.APPROVE)
        CLIENTS, WORKERS -> listOf(AdminPermissionAction.VIEW, AdminPermissionAction.OPERATE, AdminPermissionAction.DELETE, AdminPermissionAction.APPROVE)
        PRODUCTS -> listOf(AdminPermissionAction.VIEW, AdminPermissionAction.OPERATE, AdminPermissionAction.DELETE, AdminPermissionAction.APPROVE)
        SALES -> listOf(AdminPermissionAction.VIEW, AdminPermissionAction.OPERATE, AdminPermissionAction.DELETE, AdminPermissionAction.APPROVE)
        MARKETING -> listOf(AdminPermissionAction.VIEW, AdminPermissionAction.OPERATE, AdminPermissionAction.DELETE, AdminPermissionAction.APPROVE)
        CONFIG_OPS, CONFIG_BIZ, CONFIG_SEC -> listOf(AdminPermissionAction.VIEW, AdminPermissionAction.OPERATE)
        SCHEDULES, ATTENDANCE -> listOf(
            AdminPermissionAction.VIEW,
            AdminPermissionAction.OPERATE,
            AdminPermissionAction.DELETE,
            AdminPermissionAction.APPROVE,
        )
        CASH, MEMBERSHIPS -> listOf(
            AdminPermissionAction.VIEW,
            AdminPermissionAction.OPERATE,
            AdminPermissionAction.DELETE,
            AdminPermissionAction.APPROVE,
        )
    }

    fun permission(action: AdminPermissionAction): AdminSettingsPermissionModule? = when (this to action) {
        CASH to AdminPermissionAction.VIEW -> AdminSettingsPermissionModule.CASH_VIEW
        CASH to AdminPermissionAction.OPERATE -> AdminSettingsPermissionModule.CASH_OPERATE
        CASH to AdminPermissionAction.DELETE -> AdminSettingsPermissionModule.CASH_DELETE
        CASH to AdminPermissionAction.APPROVE -> AdminSettingsPermissionModule.CASH_APPROVE
        MEMBERSHIPS to AdminPermissionAction.VIEW -> AdminSettingsPermissionModule.MEMBERSHIPS_VIEW
        MEMBERSHIPS to AdminPermissionAction.OPERATE -> AdminSettingsPermissionModule.MEMBERSHIPS_OPERATE
        MEMBERSHIPS to AdminPermissionAction.DELETE -> AdminSettingsPermissionModule.MEMBERSHIPS_DELETE
        MEMBERSHIPS to AdminPermissionAction.APPROVE -> AdminSettingsPermissionModule.MEMBERSHIPS_APPROVE
        CLIENTS to AdminPermissionAction.VIEW -> AdminSettingsPermissionModule.CLIENT_VIEW
        CLIENTS to AdminPermissionAction.OPERATE -> AdminSettingsPermissionModule.CLIENT_OPERATE
        CLIENTS to AdminPermissionAction.DELETE -> AdminSettingsPermissionModule.CLIENT_DELETE
        CLIENTS to AdminPermissionAction.APPROVE -> AdminSettingsPermissionModule.CLIENT_APPROVE
        WORKERS to AdminPermissionAction.VIEW -> AdminSettingsPermissionModule.WORKER_VIEW
        WORKERS to AdminPermissionAction.OPERATE -> AdminSettingsPermissionModule.WORKER_OPERATE
        WORKERS to AdminPermissionAction.DELETE -> AdminSettingsPermissionModule.WORKER_DELETE
        WORKERS to AdminPermissionAction.APPROVE -> AdminSettingsPermissionModule.WORKER_APPROVE
        PRODUCTS to AdminPermissionAction.VIEW -> AdminSettingsPermissionModule.PRODUCT_VIEW
        PRODUCTS to AdminPermissionAction.OPERATE -> AdminSettingsPermissionModule.PRODUCT_OPERATE
        PRODUCTS to AdminPermissionAction.DELETE -> AdminSettingsPermissionModule.PRODUCT_DELETE
        PRODUCTS to AdminPermissionAction.APPROVE -> AdminSettingsPermissionModule.PRODUCT_APPROVE
        SALES to AdminPermissionAction.VIEW -> AdminSettingsPermissionModule.CASH_VIEW
        SALES to AdminPermissionAction.OPERATE -> AdminSettingsPermissionModule.CASH_OPERATE
        SALES to AdminPermissionAction.DELETE -> AdminSettingsPermissionModule.SALE_DELETE
        SALES to AdminPermissionAction.APPROVE -> AdminSettingsPermissionModule.SALE_APPROVE
        SERVICES to AdminPermissionAction.VIEW -> AdminSettingsPermissionModule.MEMBERSHIPS_VIEW
        SERVICES to AdminPermissionAction.OPERATE -> AdminSettingsPermissionModule.MEMBERSHIPS_OPERATE
        SERVICES to AdminPermissionAction.DELETE -> AdminSettingsPermissionModule.SERVICE_DELETE
        SERVICES to AdminPermissionAction.APPROVE -> AdminSettingsPermissionModule.SERVICE_APPROVE
        ORDERS to AdminPermissionAction.VIEW -> AdminSettingsPermissionModule.MEMBERSHIP_ORDER_VIEW
        ORDERS to AdminPermissionAction.OPERATE -> AdminSettingsPermissionModule.MEMBERSHIP_ORDER_OPERATE
        ORDERS to AdminPermissionAction.DELETE -> AdminSettingsPermissionModule.MEMBERSHIP_ORDER_DELETE
        ORDERS to AdminPermissionAction.APPROVE -> AdminSettingsPermissionModule.MEMBERSHIP_ORDER_APPROVE
        MARKETING to AdminPermissionAction.VIEW -> AdminSettingsPermissionModule.MARKETING_VIEW
        MARKETING to AdminPermissionAction.OPERATE -> AdminSettingsPermissionModule.MARKETING_OPERATE
        MARKETING to AdminPermissionAction.DELETE -> AdminSettingsPermissionModule.MARKETING_DELETE
        MARKETING to AdminPermissionAction.APPROVE -> AdminSettingsPermissionModule.MARKETING_APPROVE
        REPORTS_OPS to AdminPermissionAction.VIEW -> AdminSettingsPermissionModule.REPORTS_OPS_VIEW
        REPORTS_MONEY to AdminPermissionAction.VIEW -> AdminSettingsPermissionModule.REPORTS_MONEY_VIEW
        CONFIG_OPS to AdminPermissionAction.VIEW -> AdminSettingsPermissionModule.CONFIG_OPS_VIEW
        CONFIG_OPS to AdminPermissionAction.OPERATE -> AdminSettingsPermissionModule.CONFIG_OPS_EDIT
        CONFIG_BIZ to AdminPermissionAction.VIEW -> AdminSettingsPermissionModule.CONFIG_BIZ_VIEW
        CONFIG_BIZ to AdminPermissionAction.OPERATE -> AdminSettingsPermissionModule.CONFIG_BIZ_EDIT
        CONFIG_SEC to AdminPermissionAction.VIEW -> AdminSettingsPermissionModule.CONFIG_SEC_VIEW
        CONFIG_SEC to AdminPermissionAction.OPERATE -> AdminSettingsPermissionModule.CONFIG_SEC_EDIT
        SCHEDULES to AdminPermissionAction.VIEW -> AdminSettingsPermissionModule.SCHEDULE_VIEW
        SCHEDULES to AdminPermissionAction.OPERATE -> AdminSettingsPermissionModule.SCHEDULE_OPERATE
        SCHEDULES to AdminPermissionAction.DELETE -> AdminSettingsPermissionModule.SCHEDULE_DELETE
        SCHEDULES to AdminPermissionAction.APPROVE -> AdminSettingsPermissionModule.SCHEDULE_APPROVE
        ATTENDANCE to AdminPermissionAction.VIEW -> AdminSettingsPermissionModule.ATTENDANCE_VIEW
        ATTENDANCE to AdminPermissionAction.OPERATE -> AdminSettingsPermissionModule.ATTENDANCE_OPERATE
        ATTENDANCE to AdminPermissionAction.DELETE -> AdminSettingsPermissionModule.ATTENDANCE_DELETE
        ATTENDANCE to AdminPermissionAction.APPROVE -> AdminSettingsPermissionModule.ATTENDANCE_APPROVE
        else -> null
    }

}


enum class AdminPermissionAction {
    VIEW,
    OPERATE,
    DELETE,
    APPROVE,
    ;

    fun labelEs(): String = when (this) {
        VIEW -> "Ver"
        OPERATE -> "Usar"
        DELETE -> "Borrar/anular"
        APPROVE -> "Aprobar"
    }
}

enum class AdminRolePermissionTemplate {
    ADMINISTRATOR,
    SUPERVISOR,
    RECEPTION,
    TRAINER,
    ;

    fun labelEs(): String = when (this) {
        ADMINISTRATOR -> "Administrador"
        SUPERVISOR -> "Encargado"
        RECEPTION -> "Recepcionista"
        TRAINER -> "Entrenador"
    }

    fun permissions(): Set<AdminSettingsPermissionModule> = when (this) {
        ADMINISTRATOR -> AdminSettingsPermissionModule.entries.toSet()
        SUPERVISOR -> setOf(
            AdminSettingsPermissionModule.CLIENT_VIEW,
            AdminSettingsPermissionModule.CLIENT_OPERATE,
            AdminSettingsPermissionModule.WORKER_VIEW,
            AdminSettingsPermissionModule.WORKER_OPERATE,
            AdminSettingsPermissionModule.CASH_VIEW,
            AdminSettingsPermissionModule.PRODUCT_VIEW,
            AdminSettingsPermissionModule.CASH_OPERATE,
            AdminSettingsPermissionModule.PRODUCT_OPERATE,
            AdminSettingsPermissionModule.CASH_DELETE,
            AdminSettingsPermissionModule.CASH_APPROVE,
            AdminSettingsPermissionModule.MEMBERSHIPS_VIEW,
            AdminSettingsPermissionModule.MEMBERSHIPS_OPERATE,
            AdminSettingsPermissionModule.MEMBERSHIPS_DELETE,
            AdminSettingsPermissionModule.MEMBERSHIPS_APPROVE,
            AdminSettingsPermissionModule.SERVICE_DELETE,
            AdminSettingsPermissionModule.SERVICE_APPROVE,
            AdminSettingsPermissionModule.MEMBERSHIP_ORDER_DELETE,
            AdminSettingsPermissionModule.MEMBERSHIP_ORDER_APPROVE,
            AdminSettingsPermissionModule.CLIENT_DELETE,
            AdminSettingsPermissionModule.CLIENT_APPROVE,
            AdminSettingsPermissionModule.WORKER_DELETE,
            AdminSettingsPermissionModule.WORKER_APPROVE,
            AdminSettingsPermissionModule.PRODUCT_DELETE,
            AdminSettingsPermissionModule.PRODUCT_APPROVE,
            AdminSettingsPermissionModule.SALE_DELETE,
            AdminSettingsPermissionModule.SALE_APPROVE,
            AdminSettingsPermissionModule.MARKETING_DELETE,
            AdminSettingsPermissionModule.MARKETING_APPROVE,
            AdminSettingsPermissionModule.MARKETING_VIEW,
            AdminSettingsPermissionModule.MARKETING_OPERATE,
            AdminSettingsPermissionModule.REPORTS_OPS_VIEW,
            AdminSettingsPermissionModule.REPORTS_MONEY_VIEW,
            AdminSettingsPermissionModule.CONFIG_OPS_VIEW,
            AdminSettingsPermissionModule.CONFIG_OPS_EDIT,
            AdminSettingsPermissionModule.CONFIG_BIZ_VIEW,
            AdminSettingsPermissionModule.CONFIG_BIZ_EDIT,
            AdminSettingsPermissionModule.SCHEDULE_VIEW,
            AdminSettingsPermissionModule.SCHEDULE_OPERATE,
            AdminSettingsPermissionModule.SCHEDULE_DELETE,
            AdminSettingsPermissionModule.SCHEDULE_APPROVE,
            AdminSettingsPermissionModule.ATTENDANCE_VIEW,
            AdminSettingsPermissionModule.ATTENDANCE_OPERATE,
            AdminSettingsPermissionModule.ATTENDANCE_DELETE,
            AdminSettingsPermissionModule.ATTENDANCE_APPROVE,
        )
        RECEPTION -> setOf(
            AdminSettingsPermissionModule.CLIENT_VIEW,
            AdminSettingsPermissionModule.CLIENT_OPERATE,
            AdminSettingsPermissionModule.WORKER_VIEW,
            AdminSettingsPermissionModule.WORKER_OPERATE,
            AdminSettingsPermissionModule.CASH_VIEW,
            AdminSettingsPermissionModule.PRODUCT_VIEW,
            AdminSettingsPermissionModule.CASH_OPERATE,
            AdminSettingsPermissionModule.PRODUCT_OPERATE,
            AdminSettingsPermissionModule.MEMBERSHIPS_VIEW,
            AdminSettingsPermissionModule.MEMBERSHIPS_OPERATE,
            AdminSettingsPermissionModule.REPORTS_OPS_VIEW,
            AdminSettingsPermissionModule.SCHEDULE_VIEW,
            AdminSettingsPermissionModule.ATTENDANCE_VIEW,
            AdminSettingsPermissionModule.ATTENDANCE_OPERATE,
        )
        TRAINER -> setOf(
            AdminSettingsPermissionModule.CLIENT_VIEW,
            AdminSettingsPermissionModule.WORKER_VIEW,
            AdminSettingsPermissionModule.REPORTS_OPS_VIEW,
            AdminSettingsPermissionModule.SCHEDULE_VIEW,
            AdminSettingsPermissionModule.ATTENDANCE_VIEW,
        )
    }
}

data class AdminSettingsRole(
    val id: String,
    val name: String,
    val permissions: Set<AdminSettingsPermissionModule>,
    val clients: Boolean,
    val workers: Boolean,
    val payments: Boolean,
    val marketing: Boolean,
    val store: Boolean,
    val settings: Boolean,
    val membershipOverride: Boolean,
    val createdAt: String? = null,
) {
    fun has(module: AdminSettingsPermissionModule): Boolean = permissions.contains(module)
}

data class AdminSettingsRoleUpsert(
    val name: String,
    val permissions: Set<AdminSettingsPermissionModule>,
)

sealed class AuditEntry {
    abstract val id: Any
    abstract val occurredAt: String
    abstract val detail: String

    data class Staff(val event: AdminStaffAuditEvent) : AuditEntry() {
        override val id = event.id
        override val occurredAt = event.occurredAt
        override val detail = event.displayDetail()
    }
    data class AI(val log: org.gymly.ai.model.AIAuditLog) : AuditEntry() {
        override val id = log.id
        override val occurredAt = log.occurredAt
        override val detail = log.decisionSummary
    }
}

data class AdminApprovalRequest(
    val id: String,
    val type: String,
    val status: String,
    val summary: String,
    val payloadJson: String? = null,
    val requestedBy: String? = null,
    val requestedByName: String? = null,
    val reviewedBy: String? = null,
    val reviewedByName: String? = null,
    val reviewNote: String? = null,
    val createdAt: String? = null,
    val reviewedAt: String? = null,
) {
    fun typeLabelEs(): String = when (type.uppercase()) {
        "COURTESY_MEMBERSHIP" -> "Membresía cortesía"
        "DELETE_CLIENT" -> "Borrar cliente"
        "MEMBERSHIP_OVERRIDE" -> "Override membresía"
        "VOID_PAYMENT" -> "Anular cobro"
        "CONFIRM_PAYMENT" -> "Confirmar pago"
        "STORE_SALE_APPROVE" -> "Aprobar venta"
        "CASH_DISCOUNT" -> "Cobro con descuento"
        "SCHEDULE_OPERATE" -> "Horario (crear/editar)"
        "SCHEDULE_DELETE" -> "Borrar horario"
        "ATTENDANCE_OPERATE" -> "Asistencia (reserva/visita)"
        "ATTENDANCE_DELETE" -> "Borrar asistencia"
        "CLIENT_OPERATE" -> "Cliente (crear/editar)"
        "WORKER_OPERATE" -> "Trabajador (editar)"
        "WORKER_DELETE" -> "Borrar trabajador"
        "MEMBERSHIP_PLAN_OPERATE" -> "Plan de membresía (crear/editar)"
        "MEMBERSHIP_PLAN_DELETE" -> "Borrar plan de membresía"
        "PRODUCT_CATEGORY_OPERATE" -> "Categoría (crear/editar)"
        "PRODUCT_CATEGORY_DELETE" -> "Borrar categoría"
        "PRODUCT_OPERATE" -> "Producto (crear/editar)"
        "PRODUCT_DELETE" -> "Borrar producto"
        "SERVICE_OPERATE" -> "Servicio (crear/editar)"
        "SERVICE_DELETE" -> "Borrar servicio"
        "SALE_OPERATE" -> "Venta (crear/editar)"
        "SALE_DELETE" -> "Borrar venta"
        "MEMBERSHIP_ORDER_OPERATE" -> "Orden de membresía (crear/editar)"
        "MEMBERSHIP_ORDER_DELETE" -> "Borrar orden de membresía"
        "MARKETING_OPERATE" -> "Marketing (crear/editar)"
        "MARKETING_DELETE" -> "Borrar marketing"
        else -> type
    }

    fun statusLabelEs(): String = when (status.uppercase()) {
        "PENDING" -> "Pendiente"
        "APPROVED" -> "Aprobada"
        "REJECTED" -> "Rechazada"
        else -> status
    }
}

/** Acción enviada a cola de aprobación (HTTP 202). */
class AdminPendingApprovalException(
    val approvalId: String?,
    override val message: String,
) : Exception(message)

/** Seguridad SMTP en Configuración → Correo. */
enum class AdminSmtpSecurityMode {
    NONE,
    TLS,
    ;

    companion object {
        fun fromApi(value: String?): AdminSmtpSecurityMode =
            entries.firstOrNull { it.name.equals(value, ignoreCase = true) } ?: TLS

        fun fromUseTls(useTls: Boolean): AdminSmtpSecurityMode = if (useTls) TLS else NONE
    }

    fun labelEs(): String = when (this) {
        NONE -> "Sin TLS"
        TLS -> "TLS"
    }
}

data class AdminSettingsMail(
    val id: String?,
    val host: String,
    val port: Int,
    val username: String?,
    val fromAddress: String?,
    val securityMode: AdminSmtpSecurityMode,
    val useTls: Boolean,
    val passwordConfigured: Boolean = false,
    val createdAt: String? = null,
)

data class AdminSettingsMailUpsert(
    val host: String,
    val port: Int,
    val username: String? = null,
    val fromAddress: String? = null,
    val password: String? = null,
    val useTls: Boolean = true,
) {
    val securityMode: AdminSmtpSecurityMode get() = AdminSmtpSecurityMode.fromUseTls(useTls)
}

data class AdminSettingsWhatsApp(
    val id: String?,
    val enabled: Boolean,
    val apiBaseUrl: String,
    val apiVersion: String,
    val phoneNumberId: String?,
    val accessTokenConfigured: Boolean,
    val templateName: String,
    val templateLanguage: String,
    val templateWelcome: String = "client_welcome",
    val templatePaymentReceived: String = "payment_received",
    val templatePaymentPending: String = "payment_pending",
    val templateMembershipExpiring: String = "membership_expiring",
    val templateMarketing: String? = null,
    val templateTermsNotice: String? = null,
    val templateTermsNoticeApproved: Boolean = false,
    val sendHour: Int,
    val saturdayEnabled: Boolean,
    val sundayEnabled: Boolean,
    val simulateOnly: Boolean,
    val createdAt: String? = null,
    val updatedAt: String? = null,
)

data class AdminSettingsWhatsAppUpsert(
    val enabled: Boolean,
    val apiBaseUrl: String = "https://graph.facebook.com",
    val apiVersion: String = "v21.0",
    val phoneNumberId: String? = null,
    val accessToken: String? = null,
    val templateName: String = "membership_weekly_reminder",
    val templateLanguage: String = "es",
    val templateWelcome: String = "client_welcome",
    val templatePaymentReceived: String = "payment_received",
    val templatePaymentPending: String = "payment_pending",
    val templateMembershipExpiring: String = "membership_expiring",
    val templateTermsNotice: String? = null,
    val templateTermsNoticeApproved: Boolean? = null,
    val templateMarketing: String? = null,
    val sendHour: Int = 9,
    val saturdayEnabled: Boolean = true,
    val sundayEnabled: Boolean = true,
    val simulateOnly: Boolean = true,
)

data class AdminWhatsAppRunNowResult(
    val sentCount: Int,
)

data class AdminPaymentReceiptFile(
    val fileName: String,
    val mimeType: String,
    val bytes: ByteArray,
)

enum class AdminPaypalMode {
    SANDBOX,
    LIVE,
    ;

    companion object {
        fun fromApi(value: String?): AdminPaypalMode =
            entries.firstOrNull { it.name.equals(value, ignoreCase = true) } ?: SANDBOX
    }

    fun labelEs(): String = when (this) {
        SANDBOX -> "Sandbox"
        LIVE -> "Live"
    }
}

data class AdminSettingsPayment(
    val id: String?,
    val paypalEnabled: Boolean,
    val paypalClientId: String?,
    val paypalSecret: String?,
    val paypalMode: AdminPaypalMode,
    val stripeEnabled: Boolean,
    val stripePublicKey: String?,
    val stripeSecretKey: String?,
    val createdAt: String? = null,
)

data class AdminSettingsPaymentUpsert(
    val paypalEnabled: Boolean,
    val paypalClientId: String? = null,
    val paypalSecret: String? = null,
    val paypalMode: AdminPaypalMode = AdminPaypalMode.SANDBOX,
    val stripeEnabled: Boolean,
    val stripePublicKey: String? = null,
    val stripeSecretKey: String? = null,
)

data class AdminPaymentMethodType(
    val id: String,
    val code: String,
    val title: String,
    val description: String?,
    val imageUrl: String?,
    val enabled: Boolean,
    val sortOrder: Int,
    val requiresPhone: Boolean,
    val requiresReference: Boolean,
    val requiresProof: Boolean,
    val autoApprove: Boolean,
    val amountLabel: String?,
    val provider: String?,
    val providerConfig: AdminPaymentMethodProviderConfig?,
    val systemDefault: Boolean = false,
    val usesForeignCurrency: Boolean = false,
    val foreignCurrencyCode: String? = null,
    val createdAt: String? = null,
) {
    val isStripe: Boolean get() = provider == "STRIPE"
    val isPaypal: Boolean get() = provider == "PAYPAL"
}

data class AdminPaymentMethodProviderConfig(
    val provider: String,
    val enabled: Boolean,
    val publicKeyOrClientId: String?,
    val secretMasked: String?,
    val paypalMode: String?,
)

data class AdminPaymentMethodTypeUpsert(
    val title: String,
    val description: String? = null,
    val enabled: Boolean,
    val sortOrder: Int,
    val requiresPhone: Boolean,
    val requiresReference: Boolean,
    val requiresProof: Boolean,
    val autoApprove: Boolean,
    val amountLabel: String? = null,
    val provider: String? = null,
    val usesForeignCurrency: Boolean = false,
    val foreignCurrencyCode: String? = null,
)

data class AdminPaymentMethodTypeCreate(
    val code: String,
    val title: String,
    val description: String? = null,
    val enabled: Boolean = true,
    val sortOrder: Int? = null,
    val requiresPhone: Boolean = false,
    val requiresReference: Boolean = false,
    val requiresProof: Boolean = false,
    val autoApprove: Boolean = true,
    val amountLabel: String? = null,
    val provider: String? = null,
    val usesForeignCurrency: Boolean = false,
    val foreignCurrencyCode: String? = null,
)

data class AdminSettingsModules(
    val accessEnabled: Boolean,
    val exchangeRatesEnabled: Boolean,
    val suppressAccessWhileCapturing: Boolean = true,
    val clientAccountsEnabled: Boolean = false,
    val schedulesAutomatic: Boolean = false,
    /** Días de prórroga globales tras endsAt (0 = sin prórroga). */
    val membershipAccessGraceDays: Int = 0,
    val bulkPaymentsAlwaysPaid: Boolean = false,
    /** Muestra la pestaña Cotización en Configuración. No cambia los cálculos. */
    val exchangePanelVisible: Boolean = true,
)

data class AdminSettingsModulesUpsert(
    val accessEnabled: Boolean,
    val exchangeRatesEnabled: Boolean,
    val suppressAccessWhileCapturing: Boolean? = null,
    val clientAccountsEnabled: Boolean? = null,
    val schedulesAutomatic: Boolean? = null,
    val membershipAccessGraceDays: Int? = null,
    val bulkPaymentsAlwaysPaid: Boolean? = null,
    val exchangePanelVisible: Boolean? = null,
)

data class AdminPaymentMethodsBundle(
    val methods: List<AdminPaymentMethodType>,
    val gateways: AdminSettingsPayment,
)

enum class AdminAccessAttendanceMode {
    BOOKING,
    ACCESS,
    BOTH,
    ;

    fun labelEs(): String = when (this) {
        BOOKING -> "Solo clases (marcar reserva)"
        ACCESS -> "Solo entrada al gym"
        BOTH -> "Clase si hay reserva; si no, entrada"
    }

    companion object {
        fun fromApi(value: String?): AdminAccessAttendanceMode =
            entries.firstOrNull { it.name.equals(value, ignoreCase = true) } ?: BOTH
    }
}

data class AdminAccessTerminal(
    val id: String,
    val name: String,
    val enabled: Boolean,
    val host: String,
    val port: Int,
    val username: String,
    val password: String?,
    val branchId: String?,
    val branchName: String?,
    val webhookSecret: String?,
    val attendanceMode: AdminAccessAttendanceMode,
    val webhookNotifyHost: String? = null,
    val webhookNotifyPort: Int = 18080,
    val lastSeenAt: String?,
    val lastError: String?,
    val createdAt: String? = null,
    /** Última revisión de usuarios del terminal (null si nunca se revisó). */
    val lastReviewAt: String? = null,
    /** Personas que sobran o faltan según la última revisión. */
    val pendingReview: Int = 0,
)

data class AdminAccessTerminalUpsert(
    val name: String,
    val enabled: Boolean,
    val host: String,
    val port: Int,
    val username: String,
    val password: String? = null,
    val branchId: String? = null,
    val webhookSecret: String? = null,
    val attendanceMode: AdminAccessAttendanceMode = AdminAccessAttendanceMode.BOTH,
    val webhookNotifyHost: String? = null,
    val webhookNotifyPort: Int = 18080,
)

data class AdminAccessEvent(
    val id: String,
    val terminalId: String,
    val terminalName: String,
    val clientId: String?,
    val clientName: String?,
    val staffId: String? = null,
    val bookingId: String?,
    val eventTime: String?,
    val authType: String?,
    val success: Boolean,
    val result: String,
    val resultLabel: String,
)

data class AdminAccessTestResult(
    val ok: Boolean,
    val message: String,
)

data class AdminAccessSyncResult(
    val clientId: String,
    val accessSyncedAt: String?,
    val accessSyncError: String?,
)

enum class AdminNotificationEventCode {
    NEW_CLIENT_REGISTERED,
    PAYMENT_RECEIVED,
    PAYMENT_FAILED,
    PAYMENT_PENDING_APPROVAL,
    NEW_BOOKING,
    MEMBERSHIP_EXPIRING,
    NEW_INBOX_MESSAGE,
    NEW_STORE_SALE,
    LOW_STOCK_ALERT,
    ;

    companion object {
        fun fromApi(value: String): AdminNotificationEventCode =
            entries.firstOrNull { it.name.equals(value, ignoreCase = true) }
                ?: NEW_CLIENT_REGISTERED
    }

    fun labelEs(): String = when (this) {
        NEW_CLIENT_REGISTERED -> "Nuevo cliente registrado"
        PAYMENT_RECEIVED -> "Pago recibido"
        PAYMENT_FAILED -> "Pago fallido"
        PAYMENT_PENDING_APPROVAL -> "Pago pendiente de aprobación (pago móvil/transferencia/Zelle)"
        NEW_BOOKING -> "Nueva reserva"
        MEMBERSHIP_EXPIRING -> "Membresía por vencer"
        NEW_INBOX_MESSAGE -> "Nuevo mensaje en bandeja"
        NEW_STORE_SALE -> "Nueva venta en tienda"
        LOW_STOCK_ALERT -> "Alerta de poco stock"
    }
}

data class AdminSettingsNotificationRule(
    val id: String,
    val eventCode: AdminNotificationEventCode,
    val eventLabel: String,
    val emailEnabled: Boolean,
    val pushEnabled: Boolean,
    val webEnabled: Boolean,
    val createdAt: String? = null,
)

data class AdminSettingsNotificationRuleUpsert(
    val eventCode: AdminNotificationEventCode,
    val emailEnabled: Boolean,
    val pushEnabled: Boolean,
    val webEnabled: Boolean,
)

enum class AdminNotificationScheduleFrequency {
    INSTANT,
    DAILY,
    WEEKLY,
    ;

    fun labelEs(): String = when (this) {
        INSTANT -> "Al instante"
        DAILY -> "Todos los días"
        WEEKLY -> "Días de la semana"
    }

    companion object {
        fun fromApi(value: String?): AdminNotificationScheduleFrequency =
            entries.firstOrNull { it.name.equals(value, ignoreCase = true) } ?: DAILY
    }
}

data class AdminSettingsNotificationSchedule(
    val id: String?,
    val eventCode: AdminNotificationEventCode,
    val eventLabel: String,
    val enabled: Boolean,
    val frequency: AdminNotificationScheduleFrequency,
    val frequencyLabel: String,
    val sendHour: Int,
    val mondayEnabled: Boolean = false,
    val tuesdayEnabled: Boolean = false,
    val wednesdayEnabled: Boolean = false,
    val thursdayEnabled: Boolean = false,
    val fridayEnabled: Boolean = false,
    val saturdayEnabled: Boolean = false,
    val sundayEnabled: Boolean = false,
    val leadDays: String? = "7,3,1",
    val timezone: String = "America/Caracas",
    val inactivateAfterDays: Int = 0,
    val updatedAt: String? = null,
)

data class AdminSettingsNotificationScheduleUpsert(
    val eventCode: AdminNotificationEventCode,
    val enabled: Boolean,
    val frequency: AdminNotificationScheduleFrequency,
    val sendHour: Int,
    val mondayEnabled: Boolean = false,
    val tuesdayEnabled: Boolean = false,
    val wednesdayEnabled: Boolean = false,
    val thursdayEnabled: Boolean = false,
    val fridayEnabled: Boolean = false,
    val saturdayEnabled: Boolean = false,
    val sundayEnabled: Boolean = false,
    val leadDays: String? = "7,3,1",
    val timezone: String = "America/Caracas",
    val inactivateAfterDays: Int = 0,
)

data class AdminSettingsNotificationTemplate(
    val id: String,
    val eventCode: AdminNotificationEventCode,
    val eventLabel: String,
    val emailSubject: String,
    val emailBodyText: String,
    val webTitle: String,
    val webBody: String,
)

data class AdminSettingsNotificationTemplateUpsert(
    val eventCode: AdminNotificationEventCode,
    val emailSubject: String,
    val emailBodyText: String,
    val webTitle: String,
    val webBody: String,
)

data class AdminProductCategoryItem(
    val id: String,
    val name: String,
    val sortOrder: Int = 0,
    val productCount: Int = 0,
)

enum class AdminProductStockLevel {
    OK,
    LOW,
    OUT,
    ;

    companion object {
        fun fromApi(value: String?): AdminProductStockLevel =
            entries.firstOrNull { it.name.equals(value, ignoreCase = true) } ?: OK
    }

    fun labelEs(): String = when (this) {
        OK -> "OK"
        LOW -> "Poco stock"
        OUT -> "Agotado"
    }
}

data class AdminProduct(
    val id: String,
    val name: String,
    val category: String,
    val categoryLabel: String,
    val priceCents: Long,
    val priceUsdCents: Long = 0,
    val currency: String,
    val stockQty: Int,
    val minStockQty: Int = 0,
    val stockLevel: AdminProductStockLevel,
    val stockLevelLabel: String,
    val description: String?,
    val active: Boolean,
    val branchId: String?,
    val branchName: String?,
    val createdAt: String?,
    val ingredients: String? = null,
    val videoUrl: String? = null,
    val imageUrl: String? = null,
)

data class AdminProductUpsert(
    val name: String,
    val category: String,
    val priceCents: Long,
    val priceUsdCents: Long = 0,
    val currency: String = "VES",
    val stockQty: Int,
    val minStockQty: Int = 0,
    val description: String? = null,
    val active: Boolean = true,
    val branchId: String? = null,
    val ingredients: String? = null,
    val videoUrl: String? = null,
    val image: PickedImagePayload? = null,
)

enum class AdminStoreSaleMethod {
    CARD,
    CASH,
    MOBILE_PAYMENT,
    TRANSFER,
    ZELLE,
    PAYPAL,
    USDT,
    DIVISAS,
    ;

    companion object {
        fun fromApi(value: String): AdminStoreSaleMethod =
            entries.firstOrNull { it.name.equals(value, ignoreCase = true) } ?: CASH
    }

    fun labelEs(): String = when (this) {
        CARD -> "Tarjeta"
        CASH -> "Efectivo"
        MOBILE_PAYMENT -> "Pago móvil"
        TRANSFER -> "Transferencia"
        ZELLE -> "Zelle"
        PAYPAL -> "PayPal"
        USDT -> "USDT"
        DIVISAS -> "Divisas ($)"
    }

    fun requiresManualApproval(): Boolean =
        this == MOBILE_PAYMENT || this == TRANSFER || this == ZELLE || this == USDT

    fun usesDualCurrency(): Boolean = this == USDT || this == DIVISAS

    fun usesUsdAmount(): Boolean = this == DIVISAS || this == USDT || this == ZELLE

    fun requiresReference(): Boolean =
        this == MOBILE_PAYMENT || this == TRANSFER || this == ZELLE

    fun requiresProofAttachment(): Boolean = requiresManualApproval()

    fun methodHint(): String? = when (this) {
        CARD -> "Los pagos con tarjeta se procesan vía Stripe."
        PAYPAL -> "Los pagos con PayPal se liquidan automáticamente."
        DIVISAS -> "Cobro en dólares (USD) en recepción. Queda marcado como pagado al guardar."
        USDT -> "Cobro en dólares (USD) vía USDT. Indica el monto en $; no se suma a Total Bs."
        MOBILE_PAYMENT, TRANSFER, ZELLE ->
            "Puedes adjuntar referencia y/o imagen del comprobante."
        CASH -> null
    }

    /** En mostrador el cobro confirma la venta; pendiente solo vía «Pagar más tarde». */
    fun defaultCreateStatus(): AdminStoreSaleStatus = AdminStoreSaleStatus.PAID
}

/** Cliente de mostrador cuando no hay socio vinculado. */
enum class AdminStoreWalkInCustomer(val labelEs: String) {
    EXTERNAL_CLIENT("Cliente Externo"),
    ;

    companion object {
        private const val WORKER_SUFFIX = " (Trabajador)"

        fun resolveLabel(raw: String?): String {
            val trimmed = raw?.trim().orEmpty()
            if (trimmed.isEmpty()) return EXTERNAL_CLIENT.labelEs
            if (trimmed.equals("Walk-in", ignoreCase = true)
                || trimmed.equals("Walk-in / sin cliente", ignoreCase = true)
                || trimmed.equals("sin cliente", ignoreCase = true)
            ) {
                return EXTERNAL_CLIENT.labelEs
            }
            return trimmed
        }

        fun workerLabel(name: String): String {
            val trimmed = name.trim()
            if (trimmed.isEmpty()) return EXTERNAL_CLIENT.labelEs
            if (trimmed.endsWith(WORKER_SUFFIX, ignoreCase = true)) return trimmed
            return trimmed + WORKER_SUFFIX
        }

        fun stripWorkerLabel(name: String?): String {
            val trimmed = name?.trim().orEmpty()
            if (trimmed.endsWith(WORKER_SUFFIX, ignoreCase = true)) {
                return trimmed.removeSuffix(WORKER_SUFFIX).trim()
            }
            return trimmed
        }

        fun isWorkerLabel(name: String?): Boolean =
            name?.trim()?.endsWith(WORKER_SUFFIX, ignoreCase = true) == true

        fun isDefaultLabel(name: String?): Boolean =
            name.isNullOrBlank()
                || name.equals(EXTERNAL_CLIENT.labelEs, ignoreCase = true)
                || name.equals("Walk-in", ignoreCase = true)
    }
}

enum class AdminStoreSaleOrigin {
    CLIENT_AUTOPAY,
    STAFF_REGISTERED,
    ;

    companion object {
        fun fromApi(value: String): AdminStoreSaleOrigin =
            entries.firstOrNull { it.name.equals(value, ignoreCase = true) } ?: STAFF_REGISTERED
    }

    fun labelEs(): String = when (this) {
        CLIENT_AUTOPAY -> "Cliente (autopago)"
        // Quién registró la venta (mostrador), no el comprador.
        STAFF_REGISTERED -> "Mostrador"
    }
}

enum class AdminStoreSaleStatus {
    PENDING,
    PAID,
    FAILED,
    POR_VALIDAR,
    ;

    companion object {
        fun fromApi(value: String): AdminStoreSaleStatus =
            entries.firstOrNull { it.name.equals(value, ignoreCase = true) } ?: PENDING
    }

    fun labelEs(): String = when (this) {
        PENDING -> "Pendiente"
        PAID -> "Pagada"
        FAILED -> "Cancelada"
        POR_VALIDAR -> "Por validar"
    }

    fun dotColorArgb(): Long = when (this) {
        PENDING -> 0xFFE65100
        PAID -> 0xFF2E7D32
        FAILED -> 0xFFB71C1C
        POR_VALIDAR -> 0xFF1565C0
    }
}

data class AdminStoreSaleItem(
    val id: String?,
    val productId: String?,
    val productName: String,
    val qty: Int,
    val unitPriceCents: Long,
    val lineTotalCents: Long,
)

data class AdminStoreSale(
    val id: String,
    val saleNumber: Long,
    val clientId: String?,
    val customerName: String,
    val branchId: String?,
    val branchName: String?,
    val method: AdminStoreSaleMethod,
    val methodLabel: String,
    val origin: AdminStoreSaleOrigin,
    val originLabel: String,
    val status: AdminStoreSaleStatus,
    val statusLabel: String,
    val saleDate: String,
    val totalCents: Long,
    val amountPaidCents: Long = totalCents,
    val changeGivenCents: Long = 0L,
    val foreignUsdCents: Long? = null,
    val currency: String,
    val notes: String?,
    val proofUrl: String? = null,
    val proofContentType: String? = null,
    val itemsSummary: String,
    val items: List<AdminStoreSaleItem>,
    val createdAt: String?,
    /** Pendiente que falta cobrar, en Bs y en USD. La venta queda pagada cuando ambos son cero. */
    val pendingBsCents: Long = 0L,
    val pendingUsdCents: Long = 0L,
    /** Cobros registrados (una línea por método y moneda). */
    val payments: List<AdminStorePaymentLine> = emptyList(),
    /** Descuento en Bs del cobro (0 = sin descuento). */
    val discountCents: Long = 0L,
    val discountReason: String? = null,
    val discountNote: String? = null,
    /** Descuento escrito en $ (0 si fue solo en Bs). */
    val discountUsdCents: Long = 0L,
) {
    fun isProofImage(): Boolean = proofContentType?.startsWith("image/") == true
    fun isProofPdf(): Boolean = proofContentType == "application/pdf"

    /** Total a mostrar: $ si método USD (Divisas/Zelle/USDT), Bs en caso contrario. */
    fun displayTotalCents(): Long =
        if (method.usesUsdAmount()) foreignUsdCents?.takeIf { it > 0 } ?: 0L else totalCents

    fun displayTotalCurrency(): String =
        if (method.usesUsdAmount()) "USD" else currency

    /**
     * Monto de Total para listados: prioriza foreignUsdCents; si falta, suma priceUsd del catálogo.
     */
    fun displayTotalCents(products: List<AdminProduct>): Long {
        if (!method.usesUsdAmount()) return totalCents
        foreignUsdCents?.takeIf { it > 0 }?.let { return it }
        if (products.isEmpty()) return 0L
        return items.sumOf { item ->
            val pid = item.productId ?: return@sumOf 0L
            val unitUsd = products.firstOrNull { it.id == pid }?.priceUsdCents ?: 0L
            unitUsd.coerceAtLeast(0L) * item.qty.coerceAtLeast(0)
        }
    }

    fun displayTotalLabel(products: List<AdminProduct> = emptyList()): String {
        if (!method.usesUsdAmount()) {
            return formatMoney(totalCents.toInt(), currency)
        }
        val usd = displayTotalCents(products)
        return if (usd > 0L) formatMoney(usd.toInt(), "USD") else "—"
    }
}

data class AdminStoreSaleItemUpsert(
    val productId: String?,
    val productName: String? = null,
    val qty: Int,
    val unitPriceCents: Long? = null,
)

/** Línea de cobro: método, moneda ("VES" o "USD") y monto en centavos de esa moneda. */
data class AdminStorePaymentLine(
    val method: AdminStoreSaleMethod,
    val currency: String,
    val amountCents: Long,
)

data class AdminStoreSaleUpsert(
    val clientId: String? = null,
    val walkInName: String? = null,
    val branchId: String? = null,
    val method: AdminStoreSaleMethod,
    val origin: AdminStoreSaleOrigin = AdminStoreSaleOrigin.STAFF_REGISTERED,
    val status: AdminStoreSaleStatus? = null,
    val saleDate: String? = null,
    val currency: String = "VES",
    val notes: String? = null,
    val amountPaidCents: Long? = null,
    val changeGivenCents: Long = 0L,
    val foreignUsdCents: Long? = null,
    val items: List<AdminStoreSaleItemUpsert>,
    val payments: List<AdminStorePaymentLine>? = null,
    val pendingBsCents: Long = 0L,
    val pendingUsdCents: Long = 0L,
    /** Descuento en Bs del cobro de la venta; null = sin descuento. */
    val discount: AdminPaymentDiscount? = null,
)

data class AdminStoreSaleInvoiceFile(
    val fileName: String,
    val mimeType: String,
    val bytes: ByteArray,
)

enum class AdminMembershipOrderStatus {
    PENDING,
    PAID,
    COURTESY,
    ;

    companion object {
        fun fromApi(value: String): AdminMembershipOrderStatus =
            entries.firstOrNull { it.name.equals(value, ignoreCase = true) } ?: PENDING

        /** Cortesía explícita; transferencia/pago móvil en recepción quedan pagados al confirmar. */
        fun resolveFromPayment(isCourtesy: Boolean, method: AdminPaymentMethod?): AdminMembershipOrderStatus =
            when {
                isCourtesy -> COURTESY
                method == AdminPaymentMethod.TRANSFER || method == AdminPaymentMethod.MOBILE_PAYMENT -> PAID
                method?.requiresManualApproval() == true -> PENDING
                else -> PAID
            }
    }

    fun labelEs(): String = when (this) {
        PENDING -> "Pendiente"
        PAID -> "Pagado"
        COURTESY -> "Cortesía"
    }

    fun dotColorArgb(): Long = when (this) {
        PENDING -> 0xFFE65100
        PAID -> 0xFF1B5E20
        COURTESY -> 0xFF1565C0
    }

    fun activatesMembership(): Boolean = this == PAID || this == COURTESY
}

enum class AdminMembershipOrderOrigin {
    CLIENT_AUTOPAY,
    STAFF_REGISTERED,
    ;

    companion object {
        fun fromApi(value: String): AdminMembershipOrderOrigin =
            entries.firstOrNull { it.name.equals(value, ignoreCase = true) } ?: STAFF_REGISTERED
    }

    fun labelEs(): String = when (this) {
        CLIENT_AUTOPAY -> "Cliente (autopago)"
        STAFF_REGISTERED -> "Personal (registrado)"
    }
}

data class AdminMembershipOrderPayment(
    val id: String,
    val amountCents: Long,
    val currency: String,
    val method: AdminPaymentMethod?,
    val methodLabel: String,
    val status: AdminPaymentStatus,
    val statusLabel: String,
    val notes: String? = null,
    val payerPhone: String? = null,
    val proofUrl: String? = null,
    val proofContentType: String? = null,
    val paidAt: String? = null,
    val createdAt: String? = null,
    val foreignUsdCents: Long? = null,
    val collectionId: String? = null,
    val changeGivenCents: Long = 0L,
) {
    fun operationDate(): String =
        gymlyDateIsoFromInstant(paidAt) ?: gymlyDateIsoFromInstant(createdAt) ?: "—"
    fun operationDateTime(): String {
        val day = gymlyDateIsoFromInstant(paidAt) ?: gymlyDateIsoFromInstant(createdAt) ?: return "—"
        val raw = paidAt ?: createdAt ?: return day
        val time = raw.drop(11).take(5)
        return if (time.isBlank()) day else "$day $time"
    }
}

data class AdminMembershipOrder(
    val id: String,
    val orderNumber: Long,
    val clientId: String,
    val clientName: String,
    val planId: String,
    val planName: String,
    val branchId: String? = null,
    val branchName: String? = null,
    val trainerId: String? = null,
    val trainerName: String? = null,
    val method: AdminPaymentMethod? = null,
    val methodLabel: String? = null,
    val payerPhone: String? = null,
    val origin: AdminMembershipOrderOrigin,
    val originLabel: String,
    val status: AdminMembershipOrderStatus,
    val statusLabel: String,
    val orderDate: String,
    /** Fin de vigencia del plan según periodo (null si no se puede calcular). */
    val planEndsAt: String? = null,
    val amountCents: Long,
    val paidCents: Long = 0L,
    /** PAID + PENDING en la venta vinculada (reserva mientras hay aprobación de método). */
    val allocatedCents: Long = 0L,
    val planUsdCents: Long = 0L,
    val paidUsdCents: Long = 0L,
    /** Pendiente explícito en USD al cobrar; 0 = sin pendiente en $. */
    val pendingUsdCents: Long = 0L,
    /** Pendiente explícito en Bs al cobrar; 0 = sin pendiente en Bs. */
    val pendingBsCents: Long = 0L,
    val salesOrderId: String? = null,
    val currency: String,
    val notes: String? = null,
    val proofUrl: String? = null,
    val proofContentType: String? = null,
    val createdAt: String? = null,
    /** Cobros individuales (cada operación/método por separado). */
    val payments: List<AdminMembershipOrderPayment> = emptyList(),
    val changeGivenCents: Long = 0L,
    /** Descuento sobre el precio del plan (centavos); 0 = sin descuento. */
    val discountCents: Long = 0L,
    val discountReason: String? = null,
    val discountNote: String? = null,
    /** Descuento escrito en $ (0 si fue solo en Bs). */
    val discountUsdCents: Long = 0L,
) {
    fun displayNumber(): String = "#$orderNumber"
    /** Cubierto si está pagada/cortesía, o no queda pendiente ($) explícito tras cobro. */
    fun isFullyCovered(): Boolean {
        if (status == AdminMembershipOrderStatus.PAID || status == AdminMembershipOrderStatus.COURTESY) return true
        // Pendiente explícito en $ o Bs manda.
        if (pendingUsdCents > 0L || pendingBsCents > 0L) return false
        // Abono en Bs sin pendiente explícito: si no cubre el monto y la orden sigue pendiente, hay saldo.
        val paidBs = maxOf(paidCents, allocatedCents)
        if (status == AdminMembershipOrderStatus.PENDING && paidBs > 0L && paidBs < amountCents) return false
        return paidCents > 0L || paidUsdCents > 0L || allocatedCents > 0L
    }
    /** Saldo aún cobrable en Bs (pendiente Bs explícito o diferencia plan − abonado). */
    fun remainingCents(): Long {
        if (isFullyCovered()) return 0L
        if (pendingBsCents > 0L) return pendingBsCents
        return (amountCents - maxOf(paidCents, allocatedCents)).coerceAtLeast(0L)
    }
    fun remainingUsdCents(): Long {
        if (status == AdminMembershipOrderStatus.PAID || status == AdminMembershipOrderStatus.COURTESY) return 0L
        return pendingUsdCents.coerceAtLeast(0L)
    }
    fun pendingApprovalCents(): Long = (allocatedCents - paidCents).coerceAtLeast(0L)
    fun amountsSummaryLabel(): String = buildList {
        if (amountCents > 0) add(formatMoney(amountCents.toInt(), currency))
        if (planUsdCents > 0) add(formatMoney(planUsdCents.toInt(), "USD"))
    }.joinToString(" · ").ifBlank { formatMoney(0, currency) }
    fun paidSummaryLabel(): String = buildList {
        if (paidCents > 0) add(formatMoney(paidCents.toInt(), currency))
        if (paidUsdCents > 0) add(formatMoney(paidUsdCents.toInt(), "USD"))
    }.joinToString(" · ").ifBlank { formatMoney(0, currency) }
    fun methodsSummary(): String? {
        if (payments.isNotEmpty()) {
            return payments.map { it.methodLabel }.filter { it.isNotBlank() }.distinct().joinToString(" + ")
                .ifBlank { null }
        }
        return methodLabel
    }
}

data class AdminMembershipOrderUpsert(
    val clientId: String,
    val planId: String,
    val branchId: String? = null,
    val trainerId: String? = null,
    val method: AdminPaymentMethod? = null,
    val payerPhone: String? = null,
    val origin: AdminMembershipOrderOrigin = AdminMembershipOrderOrigin.STAFF_REGISTERED,
    val status: AdminMembershipOrderStatus? = null,
    val orderDate: String? = null,
    val amountCents: Long? = null,
    val pendingUsdCents: Long? = null,
    val pendingBsCents: Long? = null,
    val currency: String = "VES",
    val notes: String? = null,
    /** Cobro de alta masiva: el servidor lo aplica al aprobar la orden si queda en aprobación. */
    val collect: AdminPaymentCollect? = null,
    /** Fechas de cobro a corregir en el mismo guardado: van con el entrenador en una sola aprobación. */
    val paymentDates: List<AdminMembershipOrderPaymentDate> = emptyList(),
    /** Solo cortesía: fecha de vencimiento que escribe quien la registra (ISO yyyy-MM-dd). */
    val endsAt: String? = null,
)

data class AdminMembershipOrderPaymentDate(
    val paymentId: String,
    val paidAt: String,
)

data class AdminClient(
    val id: String,
    val personId: String,
    val firstName: String,
    val lastName: String,
    val email: String?,
    val phone: String?,
    val docId: String? = null,
    val sex: AdminPersonSex? = null,
    val address: String? = null,
    val status: AdminClientStatus,
    val notes: String?,
    val photoUrl: String? = null,
    val portalEnabled: Boolean,
    val branchId: String?,
    val branchName: String?,
    val createdAt: String?,
    val accessSyncedAt: String? = null,
    val accessSyncError: String? = null,
    val membershipPlanName: String? = null,
    /** WEB / ANDROID / IOS: canales por los que el socio entró alguna vez. */
    val accessChannels: List<String> = emptyList(),
    /** Código del motivo de inactivación (BAJA_VOLUNTARIA, …); null si está activo. */
    val inactivationReason: String? = null,
    val inactivationNote: String? = null,
) {
    val fullName: String get() = "$firstName $lastName".trim()
}

data class AdminClientUpsert(
    val firstName: String,
    val lastName: String,
    val email: String?,
    val status: AdminClientStatus,
    val notes: String? = null,
    val portalPassword: String? = null,
    val branchId: String? = null,
    val disablePortal: Boolean = false,
    val phone: String? = null,
    val docId: String? = null,
    val sex: AdminPersonSex? = null,
    val address: String = "",
    val inactivationReason: String? = null,
    val inactivationNote: String? = null,
)

enum class AdminPersonSex {
    MALE,
    FEMALE,
    OTHER,
    ;

    companion object {
        fun fromApi(value: String?): AdminPersonSex? =
            value?.trim()?.uppercase()?.let { v ->
                entries.firstOrNull { it.name.equals(v, ignoreCase = true) }
            }
    }

    fun labelEs(): String = when (this) {
        MALE -> "Masculino"
        FEMALE -> "Femenino"
        OTHER -> "Otro"
    }
}

enum class AdminStaffRole {
    ADMIN,
    SUPERVISOR,
    TRAINER,
    RECEPTION,
    PLATFORM,
    ;

    companion object {
        fun fromApi(value: String): AdminStaffRole =
            entries.firstOrNull { it.name.equals(value, ignoreCase = true) } ?: RECEPTION
    }

    fun labelEs(): String = when (this) {
        ADMIN -> "Administrador"
        SUPERVISOR -> "Encargado"
        TRAINER -> "Entrenador"
        RECEPTION -> "Recepción"
        PLATFORM -> "Plataforma Gymly"
    }

    fun dotColorArgb(): Long = when (this) {
        ADMIN -> 0xFF3B4CB8
        SUPERVISOR -> 0xFFC2410C
        TRAINER -> 0xFF2E7D32
        RECEPTION -> 0xFF7C3AED
        PLATFORM -> 0xFF0F766E
    }
}

data class AdminWorkTimeBlock(
    val startTime: String,
    val endTime: String,
)

data class AdminWorkDaySchedule(
    val weekday: Int,
    val blocks: List<AdminWorkTimeBlock> = emptyList(),
)

data class AdminWorker(
    val id: String,
    val displayName: String,
    val email: String?,
    val phone: String? = null,
    val docId: String? = null,
    val role: AdminStaffRole,
    val active: Boolean,
    val branchId: String?,
    val branchName: String?,
    val createdAt: String?,
    val sex: AdminPersonSex? = null,
    val address: String? = null,
    val photoUrl: String? = null,
    /** Jornada: días con uno o más bloques horarios. */
    val workSchedule: List<AdminWorkDaySchedule> = emptyList(),
    val serviceIds: List<String> = emptyList(),
    /** Código del motivo de inactivación (RENUNCIA, DESPIDO, …); null si está activo. */
    val inactivationReason: String? = null,
    val inactivationNote: String? = null,
) {
    val firstName: String get() = displayName.trim().substringBefore(' ').ifBlank { displayName }
    val lastName: String get() = displayName.trim().substringAfter(' ', "").ifBlank { "—" }
    val workDays: List<Int> get() = workSchedule.map { it.weekday }.sorted()
}

data class AdminWorkerUpsert(
    val displayName: String,
    val email: String?,
    val phone: String? = null,
    val docId: String? = null,
    val role: AdminStaffRole,
    val password: String? = null,
    val active: Boolean = true,
    val branchId: String? = null,
    val sex: AdminPersonSex,
    val address: String,
    val workSchedule: List<AdminWorkDaySchedule> = emptyList(),
    val serviceIds: List<String> = emptyList(),
    val inactivationReason: String? = null,
    val inactivationNote: String? = null,
)

/** Una fila de la revisión de un terminal: un código que sobra o que falta frente a Gymly. */
data class AdminTerminalReconcileItem(
    val employeeNo: String,
    val name: String,
    /** SOCIO, TRABAJADOR o SIN_FICHA. */
    val kind: String,
    val reason: String,
    /** DELETE (quitar del terminal) o SYNC (enviar al terminal). */
    val action: String,
)

data class AdminTerminalReconcileReport(
    val terminalId: String,
    val terminalName: String,
    val reviewedAt: String,
    val onTerminal: Int,
    val expected: Int,
    val sobran: List<AdminTerminalReconcileItem>,
    val faltan: List<AdminTerminalReconcileItem>,
)

data class AdminTerminalReconcileResult(val applied: Int, val errors: List<String>)

data class AdminTerminalReconcileAction(val employeeNo: String, val action: String)

enum class AdminServiceType {
    CLASS,
    SESSION,
    ;

    companion object {
        fun fromApi(value: String): AdminServiceType =
            when (value.trim().uppercase()) {
                "CLASS", "CLASE" -> CLASS
                "SESSION", "SESION", "SESIÓN", "TRAINING", "ENTRENAMIENTO" -> SESSION
                else -> entries.firstOrNull { it.name.equals(value, ignoreCase = true) } ?: CLASS
            }
    }

    fun labelEs(): String = when (this) {
        CLASS -> "Clase"
        SESSION -> "Entrenamiento"
    }

    fun dotColorArgb(): Long = when (this) {
        CLASS -> 0xFF2E7D32
        SESSION -> 0xFF7C3AED
    }
}

data class AdminCatalogService(
    val id: String,
    val name: String,
    val type: AdminServiceType,
    val durationMin: Int?,
    val priceCents: Long,
    val active: Boolean,
    val createdAt: String?,
    val description: String? = null,
    val imageUrl: String? = null,
)

data class AdminCatalogServiceUpsert(
    val name: String,
    val type: AdminServiceType,
    val durationMin: Int?,
    val active: Boolean = true,
    val description: String? = null,
    val image: PickedImagePayload? = null,
)

enum class AdminBillingPeriod {
    DAILY,
    WEEKLY,
    MONTHLY,
    ANNUAL,
    /** Cortesía: sin cobro y sin periodo fijo; el vencimiento lo define quien la registra. */
    COURTESY,
    ;

    companion object {
        /** Periodos disponibles al crear/editar un plan (sin anual). */
        fun choosable(): List<AdminBillingPeriod> = listOf(DAILY, WEEKLY, MONTHLY, COURTESY)

        fun fromApi(value: String): AdminBillingPeriod =
            when (value.trim().uppercase()) {
                "DAILY", "DIARIO", "DAY" -> DAILY
                "WEEKLY", "SEMANAL" -> WEEKLY
                "MONTHLY", "MENSUAL" -> MONTHLY
                "ANNUAL", "ANUAL", "YEARLY" -> ANNUAL
                "COURTESY", "CORTESIA", "CORTESÍA" -> COURTESY
                else -> entries.firstOrNull { it.name.equals(value, ignoreCase = true) } ?: MONTHLY
            }
    }

    fun labelEs(): String = when (this) {
        DAILY -> "Diario"
        WEEKLY -> "Semanal"
        MONTHLY -> "Mensual"
        ANNUAL -> "Anual"
        COURTESY -> "Cortesía"
    }

    /** Defaults de avisos de vencimiento (días antes; 0 = día del vencimiento). */
    fun defaultReminderLeadDays(): String = when (this) {
        DAILY -> "0"
        WEEKLY -> "1,0"
        MONTHLY -> "7,3,1,0"
        ANNUAL -> "30,7,3,1,0"
        COURTESY -> "0"
    }

    fun dotColorArgb(): Long = when (this) {
        DAILY -> 0xFF0288D1
        WEEKLY -> 0xFFF59E0B
        MONTHLY -> 0xFF2E7D32
        ANNUAL -> 0xFF7C3AED
        COURTESY -> 0xFF607D8B
    }
}

enum class AdminScheduleType {
    DAY,
    NIGHT,
    MIXED,
    ;

    companion object {
        fun fromApi(value: String): AdminScheduleType =
            when (value.trim().uppercase()) {
                "DAY", "DIURNO" -> DAY
                "NIGHT", "NOCTURNO" -> NIGHT
                "MIXED", "MIXTO" -> MIXED
                else -> entries.firstOrNull { it.name.equals(value, ignoreCase = true) } ?: MIXED
            }
    }

    fun labelEs(): String = when (this) {
        DAY -> "Diurno"
        NIGHT -> "Nocturno"
        MIXED -> "Mixto"
    }

    fun badgeBgArgb(): Long = when (this) {
        DAY -> 0xFFFFF3E0
        NIGHT -> 0xFFFCE4EC
        MIXED -> 0xFFE3F2FD
    }

    fun badgeFgArgb(): Long = when (this) {
        DAY -> 0xFFE65100
        NIGHT -> 0xFFC2185B
        MIXED -> 0xFF1565C0
    }
}

private fun normalizePlanNameForOrders(name: String): String =
    name.trim().lowercase()
        .replace('á', 'a').replace('é', 'e').replace('í', 'i').replace('ó', 'o').replace('ú', 'u')

private val ORDER_PLAN_EXCLUDED_NAMES = setOf(
    "monthly unlimited",
    "atencion con entrenador",
)

data class AdminMembershipPlanItem(
    val id: String,
    val name: String,
    val billingPeriod: AdminBillingPeriod,
    val scheduleType: AdminScheduleType,
    val priceCents: Long,
    val priceUsdCents: Long = 0,
    val active: Boolean,
    val services: List<AdminPlanServiceSummary> = emptyList(),
    val createdAt: String?,
    /** Override opcional: "7,3,1,0". Null = defaults del periodo. */
    val reminderLeadDays: String? = null,
    val effectiveReminderLeadDays: String? = null,
) {
    /** Planes activos válidos para órdenes / alta masiva (excluye legacy inactivos en catálogo). */
    fun selectableForOrders(): Boolean {
        if (!active) return false
        return normalizePlanNameForOrders(name) !in ORDER_PLAN_EXCLUDED_NAMES
    }

    /** Nombre antiguo que solo repite el periodo (p. ej. "semanal", "Diario"). */
    fun isPeriodOnlyLegacyName(): Boolean =
        normalizePlanNameForOrders(name) == normalizePlanNameForOrders(billingPeriod.labelEs())
}

fun List<AdminMembershipPlanItem>.selectableForOrders(): List<AdminMembershipPlanItem> {
    val base = filter { it.selectableForOrders() }
    val periodsWithNamedPlan = base
        .filterNot { it.isPeriodOnlyLegacyName() }
        .map { it.billingPeriod }
        .toSet()
    return base.filter { plan ->
        !plan.isPeriodOnlyLegacyName() || plan.billingPeriod !in periodsWithNamedPlan
    }
}

data class AdminPlanServiceSummary(
    val id: String,
    val name: String,
    val type: AdminServiceType,
)

data class AdminMembershipPlanUpsert(
    val name: String,
    val billingPeriod: AdminBillingPeriod,
    val scheduleType: AdminScheduleType,
    val priceCents: Long,
    val priceUsdCents: Long = 0,
    val active: Boolean = true,
    val serviceIds: List<String> = emptyList(),
    val reminderLeadDays: String? = null,
)

enum class AdminMembershipStatus {
    ACTIVE,
    EXPIRED,
    CANCELLED,
    ;

    companion object {
        fun fromApi(value: String): AdminMembershipStatus =
            when (value.trim().uppercase()) {
                "ACTIVE", "ACTIVA" -> ACTIVE
                "EXPIRED", "VENCIDA" -> EXPIRED
                "CANCELLED", "CANCELED", "CANCELADA" -> CANCELLED
                else -> entries.firstOrNull { it.name.equals(value, ignoreCase = true) } ?: ACTIVE
            }
    }

    fun labelEs(): String = when (this) {
        ACTIVE -> "Activa"
        EXPIRED -> "Vencida"
        CANCELLED -> "Cancelada"
    }

    fun dotColorArgb(): Long = when (this) {
        ACTIVE -> 0xFF2E7D32
        EXPIRED -> 0xFF757575
        CANCELLED -> 0xFFC62828
    }

    fun badgeBgArgb(): Long = when (this) {
        ACTIVE -> 0xFFE8F5E9
        EXPIRED -> 0xFFF3F4F6
        CANCELLED -> 0xFFFCE8E6
    }

    fun badgeFgArgb(): Long = when (this) {
        ACTIVE -> 0xFF2E7D32
        EXPIRED -> 0xFF616161
        CANCELLED -> 0xFFC62828
    }
}

enum class AdminTrainingAttendance {
    ATTENDED,
    MISSED,
    CANCELLED,
    ;

    companion object {
        fun fromApi(value: String): AdminTrainingAttendance =
            entries.firstOrNull { it.name.equals(value, ignoreCase = true) } ?: ATTENDED
    }

    fun labelEs(): String = when (this) {
        ATTENDED -> "Asistida"
        MISSED -> "No asistida"
        CANCELLED -> "Cancelada"
    }
}

enum class AdminMembershipAccessMode {
    AUTO,
    MANUAL_ACTIVE,
    MANUAL_BLOCKED,
    ;

    companion object {
        fun fromApi(value: String): AdminMembershipAccessMode =
            when (value.trim().uppercase()) {
                "AUTO" -> AUTO
                "MANUAL_ACTIVE" -> MANUAL_ACTIVE
                "MANUAL_BLOCKED" -> MANUAL_BLOCKED
                else -> entries.firstOrNull { it.name.equals(value, ignoreCase = true) } ?: AUTO
            }
    }

    fun labelEs(): String = when (this) {
        AUTO -> "Automática"
        MANUAL_ACTIVE -> "Activa manual"
        MANUAL_BLOCKED -> "Bloqueada manual"
    }
}

data class AdminMembershipItem(
    val id: String,
    val clientId: String,
    val clientName: String,
    val planId: String,
    val planName: String,
    val status: AdminMembershipStatus,
    val startsAt: String,
    val endsAt: String?,
    val branchId: String?,
    val branchName: String?,
    val trainerId: String?,
    val trainerName: String?,
    val accessMode: AdminMembershipAccessMode = AdminMembershipAccessMode.AUTO,
    val manualReason: String? = null,
    val manualByStaffName: String? = null,
    val createdAt: String?,
)

data class AdminMembershipUpsert(
    val clientId: String,
    val planId: String,
    val status: AdminMembershipStatus = AdminMembershipStatus.ACTIVE,
    val startsAt: String,
    val endsAt: String? = null,
    val branchId: String? = null,
    val trainerId: String? = null,
    val manualReason: String? = null,
)

enum class AdminMembershipAccessEventType {
    PAYMENT_RENEWAL,
    COURTESY_ACTIVATION,
    MANUAL_ACTIVATION,
    MANUAL_BLOCK,
    RELEASE_AUTOMATIC,
    ;

    companion object {
        fun fromApi(value: String): AdminMembershipAccessEventType =
            entries.firstOrNull { it.name.equals(value, ignoreCase = true) } ?: PAYMENT_RENEWAL
    }

    fun labelEs(): String = when (this) {
        PAYMENT_RENEWAL -> "Renovación por pago"
        COURTESY_ACTIVATION -> "Activación por cortesía"
        MANUAL_ACTIVATION -> "Activación manual"
        MANUAL_BLOCK -> "Bloqueo manual"
        RELEASE_AUTOMATIC -> "Vuelta a acceso automático"
    }
}

data class AdminMembershipAccessHistoryItem(
    val id: String,
    val eventType: AdminMembershipAccessEventType,
    val eventLabel: String,
    val planName: String,
    val trainerName: String?,
    val status: AdminMembershipStatus,
    val startsAt: String,
    val endsAt: String?,
    val accessMode: AdminMembershipAccessMode?,
    val reason: String?,
    val staffName: String?,
    val orderNumber: Long?,
    val amountCents: Long?,
    val occurredAt: String,
)

enum class AdminOrderStatus {
    DRAFT,
    OPEN,
    PAID,
    CANCELLED,
    ;

    companion object {
        fun fromApi(value: String): AdminOrderStatus =
            when (value.trim().uppercase()) {
                "DRAFT" -> DRAFT
                "OPEN", "PENDING", "PENDIENTE" -> OPEN
                "PAID", "PAGADA" -> PAID
                "CANCELLED", "CANCELED", "CANCELADA" -> CANCELLED
                else -> entries.firstOrNull { it.name.equals(value, ignoreCase = true) } ?: OPEN
            }
    }

    fun labelEs(): String = when (this) {
        DRAFT, OPEN -> "Pendiente"
        PAID -> "Pagada"
        CANCELLED -> "Cancelada"
    }

    fun isPending(): Boolean = this == DRAFT || this == OPEN

    fun dotColorArgb(): Long = when (this) {
        DRAFT, OPEN -> 0xFFF59E0B
        PAID -> 0xFF2E7D32
        CANCELLED -> 0xFFC62828
    }

    fun badgeBgArgb(): Long = when (this) {
        DRAFT, OPEN -> 0xFFFFF3E0
        PAID -> 0xFFE8F5E9
        CANCELLED -> 0xFFFCE8E6
    }

    fun badgeFgArgb(): Long = when (this) {
        DRAFT, OPEN -> 0xFFE65100
        PAID -> 0xFF2E7D32
        CANCELLED -> 0xFFC62828
    }
}

data class AdminOrderItem(
    val id: String,
    val serviceId: String?,
    val serviceName: String?,
    val description: String?,
    val qty: Int,
    val unitPriceCents: Long,
) {
    fun displayLabel(): String =
        description?.takeIf { it.isNotBlank() }
            ?: serviceName?.takeIf { it.isNotBlank() }
            ?: "Ítem"
}

data class AdminOrder(
    val id: String,
    val orderNumber: Long?,
    val clientId: String,
    val clientName: String,
    val status: AdminOrderStatus,
    val totalAmountCents: Long,
    val createdAt: String?,
    val lines: List<AdminOrderItem>,
) {
    fun displayNumber(): String = orderNumber?.let { "#$it" } ?: "#${id.take(4)}"

    fun itemsSummary(): String =
        lines.joinToString(", ") { it.displayLabel() }.ifBlank { "—" }

    fun orderDate(): String = createdAt?.take(10) ?: "—"
}

data class AdminOrderUpsert(
    val clientId: String,
    val status: AdminOrderStatus = AdminOrderStatus.OPEN,
    val itemsLabel: String,
    val totalAmountCents: Long,
)

enum class AdminPaymentMethod {
    CARD,
    TRANSFER,
    MOBILE_PAYMENT,
    ZELLE,
    BIZUM,
    CASH,
    ONLINE,
    USDT,
    DIVISAS,
    ;

    companion object {
        fun fromApi(value: String): AdminPaymentMethod =
            entries.firstOrNull { it.name.equals(value, ignoreCase = true) } ?: CARD

        fun adminChoices(): List<AdminPaymentMethod> =
            listOf(CASH, CARD, TRANSFER, MOBILE_PAYMENT, ZELLE, BIZUM, USDT, DIVISAS)
    }

    fun labelEs(): String = when (this) {
        CARD -> "Tarjeta"
        TRANSFER -> "Transferencia"
        MOBILE_PAYMENT -> "Pago móvil"
        ZELLE -> "Zelle"
        BIZUM -> "Bizum"
        CASH -> "Efectivo"
        ONLINE -> "Online"
        USDT -> "USDT"
        DIVISAS -> "Divisas ($)"
    }

    fun requiresManualApproval(): Boolean =
        this == MOBILE_PAYMENT || this == TRANSFER || this == ZELLE || this == BIZUM || this == USDT

    fun usesDualCurrency(): Boolean = this == DIVISAS || this == USDT

    /** Cobro en dólares en recepción (Zelle, USDT, Divisas). */
    fun usesUsdAmount(): Boolean = this == DIVISAS || this == USDT || this == ZELLE

    fun requiresReference(): Boolean =
        this == MOBILE_PAYMENT || this == TRANSFER || this == ZELLE || this == BIZUM

    fun requiresProofAttachment(): Boolean =
        requiresReference() || this == USDT

    /** @deprecated Use [requiresReference] or [requiresProofAttachment]. */
    fun requiresProofOrReference(): Boolean = requiresProofAttachment()

    fun amountInputLabel(): String = when (this) {
        DIVISAS, USDT, ZELLE -> "Monto (USD)"
        else -> "Monto (${GymlyCurrency.SYMBOL})"
    }

    /** Métodos donde se muestra el precio del plan en dólares. */
    fun showsPlanUsdReference(): Boolean = usesUsdAmount()

    fun requiresPayerPhone(): Boolean = false

    fun methodHint(): String? = when (this) {
        CASH -> "Cobro en efectivo en recepción. Queda marcado como pagado al guardar."
        CARD -> "Los pagos con tarjeta se procesan vía Stripe."
        DIVISAS -> "Cobro en dólares (USD) en recepción. Queda marcado como pagado al guardar."
        USDT -> "Cobro en dólares (USD) vía USDT. Indica el monto en $; no se suma a Total Bs."
        MOBILE_PAYMENT ->
            "Indica los últimos 5 dígitos de la referencia. Queda pagado al confirmar en recepción."
        TRANSFER ->
            "Indica la referencia de la transferencia. Queda pagado al confirmar en recepción."
        ZELLE, BIZUM ->
            "Puedes adjuntar referencia y/o imagen del comprobante."
        else -> null
    }

    fun defaultCreateStatus(): AdminPaymentStatus =
        if (requiresManualApproval()) AdminPaymentStatus.PENDING else AdminPaymentStatus.PAID
}

enum class AdminPaymentStatus {
    PENDING,
    PAID,
    FAILED,
    REFUNDED,
    POR_VALIDAR,
    ;

    companion object {
        fun fromApi(value: String): AdminPaymentStatus =
            when (value.trim().uppercase()) {
                "PENDING", "PENDIENTE" -> PENDING
                "PAID", "PAGADO" -> PAID
                "FAILED", "FALLIDO" -> FAILED
                "REFUNDED", "REEMBOLSADO" -> REFUNDED
                else -> entries.firstOrNull { it.name.equals(value, ignoreCase = true) } ?: PENDING
            }

        fun adminChoices(): List<AdminPaymentStatus> = listOf(PENDING, PAID, FAILED)
    }

    fun labelEs(): String = when (this) {
        PENDING -> "Pendiente"
        PAID -> "Pagado"
        FAILED -> "Fallido"
        REFUNDED -> "Reembolsado"
        POR_VALIDAR -> "Por validar"
    }

    fun dotColorArgb(): Long = when (this) {
        PENDING -> 0xFFF59E0B
        PAID -> 0xFF2E7D32
        FAILED -> 0xFFC62828
        REFUNDED -> 0xFF616161
        POR_VALIDAR -> 0xFF1565C0
    }

    fun badgeBgArgb(): Long = when (this) {
        PENDING -> 0xFFFFF3E0
        PAID -> 0xFFE8F5E9
        FAILED -> 0xFFFCE8E6
        REFUNDED -> 0xFFF3F4F6
        POR_VALIDAR -> 0xFFE3F2FD
    }

    fun badgeFgArgb(): Long = when (this) {
        PENDING -> 0xFFE65100
        PAID -> 0xFF2E7D32
        FAILED -> 0xFFC62828
        REFUNDED -> 0xFF616161
        POR_VALIDAR -> 0xFF1565C0
    }
}

enum class AdminPaymentOrigin {
    STAFF_REGISTERED,
    CLIENT_AUTOPAY,
    ;

    companion object {
        fun fromApi(value: String?): AdminPaymentOrigin =
            when (value?.trim()?.uppercase()) {
                "CLIENT_AUTOPAY", "AUTOPAGO", "CLIENT" -> CLIENT_AUTOPAY
                else -> STAFF_REGISTERED
            }
    }

    fun labelEs(): String = when (this) {
        STAFF_REGISTERED -> "Personal (registrado)"
        CLIENT_AUTOPAY -> "Cliente (autopago)"
    }

    fun badgeBgArgb(): Long = when (this) {
        CLIENT_AUTOPAY -> 0xFFE3F2FD
        STAFF_REGISTERED -> 0xFFF3F4F6
    }

    fun badgeFgArgb(): Long = when (this) {
        CLIENT_AUTOPAY -> 0xFF1565C0
        STAFF_REGISTERED -> 0xFF616161
    }

    fun isAutopay(): Boolean = this == CLIENT_AUTOPAY
}

data class AdminPayment(
    val id: String,
    val orderId: String,
    val clientId: String,
    val clientName: String,
    val amountCents: Long,
    val currency: String,
    val method: AdminPaymentMethod,
    val status: AdminPaymentStatus,
    val origin: AdminPaymentOrigin,
    val notes: String?,
    val payerPhone: String? = null,
    val proofUrl: String? = null,
    val proofContentType: String? = null,
    val paidAt: String?,
    val createdAt: String?,
    val foreignUsdCents: Long? = null,
    val foreignEurCents: Long? = null,
    val exchangeEffectiveDate: String? = null,
) {
    fun paymentDate(): String = gymlyDateIsoFromInstant(paidAt) ?: "—"
    fun isProofImage(): Boolean = proofContentType?.startsWith("image/") == true
    fun isProofPdf(): Boolean = proofContentType == "application/pdf"
}

data class AdminPaymentUpsert(
    val clientId: String? = null,
    val orderId: String? = null,
    val method: AdminPaymentMethod,
    val amountCents: Long,
    val status: AdminPaymentStatus = AdminPaymentStatus.PAID,
    val currency: String = "VES",
    val notes: String? = null,
    val payerPhone: String? = null,
    /** ISO-8601 instant or yyyy-MM-dd */
    val paidAt: String? = null,
    val planId: String? = null,
    val branchId: String? = null,
    val trainerId: String? = null,
)

data class AdminPaymentCollectLine(
    val method: AdminPaymentMethod,
    val amountCents: Long,
    val foreignUsdCents: Long? = null,
    val payerPhone: String? = null,
    val notes: String? = null,
    /** Comprobante ya guardado en el servidor (cobro en aprobación de alta masiva). */
    val proofPath: String? = null,
)

data class AdminPaymentCollectTarget(
    val orderId: String? = null,
    val clientId: String? = null,
    val membershipOrderId: String? = null,
    val amountCents: Long,
    val planId: String? = null,
    val branchId: String? = null,
    val trainerId: String? = null,
    /** Descuento de esta persona dentro del cobro (alta masiva). Null = sin descuento. */
    val discount: AdminPaymentDiscount? = null,
)

data class AdminPaymentCollect(
    val currency: String = "VES",
    val targets: List<AdminPaymentCollectTarget>,
    val lines: List<AdminPaymentCollectLine>,
    val paidAt: String? = null,
    /** Vuelto en Bs. */
    val changeGivenCents: Long = 0L,
    /** Vuelto en USD (excedente Zelle / USDT / Divisas). */
    val changeGivenUsdCents: Long = 0L,
    /** Descuento en Bs sobre el plan (solo cobro de orden de membresía). Null = sin descuento. */
    val discount: AdminPaymentDiscount? = null,
)

/** Descuento en Bs de un cobro: motivo (PRONTO_PAGO, PROMOCION, EXCEPCION, ERROR_PRECIO, OTRO) y nota opcional. */
data class AdminPaymentDiscount(
    /** Descuento en Bs (con la tasa encendida el backend lo recalcula desde [usdCents]). */
    val cents: Long,
    val reason: String,
    val note: String? = null,
    /** Descuento escrito en $; null si fue solo en Bs. */
    val usdCents: Long? = null,
)

enum class AdminPromotionStatus {
    ACTIVE,
    INACTIVE,
    PENDING,
    ;

    fun labelEs(): String = when (this) {
        ACTIVE -> "Activo"
        INACTIVE -> "Inactivo"
        PENDING -> "Pendiente de aprobación"
    }

    fun dotColorArgb(): Long = when (this) {
        ACTIVE -> 0xFF2E7D32
        INACTIVE -> 0xFF9E9E9E
        PENDING -> 0xFFEF6C00
    }

    fun badgeBgArgb(): Long = when (this) {
        ACTIVE -> 0xFFE8F5E9
        INACTIVE -> 0xFFF3F4F6
        PENDING -> 0xFFFFF3E0
    }

    fun badgeFgArgb(): Long = when (this) {
        ACTIVE -> 0xFF2E7D32
        INACTIVE -> 0xFF616161
        PENDING -> 0xFFE65100
    }
}

data class AdminPromotionSlide(
    val id: String,
    val sortIndex: Int,
    val fullUrl: String,
    val thumbUrl: String,
)

enum class AdminMarketingChannel {
    APP,
    WHATSAPP,
    BOTH,
    ;

    fun labelEs(): String = when (this) {
        APP -> "Solo app"
        WHATSAPP -> "Solo WhatsApp"
        BOTH -> "App y WhatsApp"
    }

    fun includesApp(): Boolean = this == APP || this == BOTH
    fun includesWhatsApp(): Boolean = this == WHATSAPP || this == BOTH

    companion object {
        fun fromApi(value: String?): AdminMarketingChannel =
            entries.firstOrNull { it.name.equals(value?.trim(), ignoreCase = true) } ?: APP
    }
}

data class AdminPromotion(
    val id: String,
    val title: String,
    val details: String?,
    val displayType: String,
    val channel: AdminMarketingChannel = AdminMarketingChannel.APP,
    val active: Boolean,
    val sortOrder: Int,
    val startsAt: String?,
    val endsAt: String?,
    val scheduledAt: String? = null,
    val whatsappSendStatus: String? = null,
    val whatsappSentAt: String? = null,
    val whatsappError: String? = null,
    val createdAt: String?,
    val slides: List<AdminPromotionSlide>,
    val videoUrl: String? = null,
    val pendingApproval: Boolean = false,
) {
    /** Imagen que muestran el detalle y la edición: la de menor orden. Null si es video o no hay banner. */
    fun portadaImagen(): AdminPromotionSlide? =
        if (displayType == "VIDEO" || !videoUrl.isNullOrBlank()) null
        else slides.minByOrNull { it.sortIndex }

    fun status(): AdminPromotionStatus = when {
        pendingApproval -> AdminPromotionStatus.PENDING
        active -> AdminPromotionStatus.ACTIVE
        else -> AdminPromotionStatus.INACTIVE
    }

    fun dateRangeLabel(): String {
        if (!channel.includesApp()) return "—"
        val from = startsAt?.take(10) ?: "—"
        val to = endsAt?.take(10) ?: "—"
        return "$from — $to"
    }

    fun scheduledLabel(): String {
        if (!channel.includesWhatsApp()) return "—"
        val at = scheduledAt?.replace('T', ' ')?.take(16) ?: return "Sin hora"
        return at
    }

    fun bannerThumbUrl(): String? = slides.minByOrNull { it.sortIndex }?.thumbUrl
}

data class AdminPromotionUpsert(
    val title: String,
    val channel: AdminMarketingChannel = AdminMarketingChannel.APP,
    val startsAt: String?,
    val endsAt: String?,
    val scheduledAt: String? = null,
    val active: Boolean = true,
    val details: String? = null,
    val displayType: String = "SINGLE",
    val sortOrder: Int = 0,
    val banner: PickedImagePayload? = null,
    val videoUrl: String? = null,
)

data class PickedImagePayload(
    val bytes: ByteArray,
    val fileName: String,
    val mimeType: String,
)

enum class AdminReportKind {
    CASH_RECONCILIATION,
    SESSIONS,
    MEMBERSHIPS,
    ENROLLMENTS,
    SALES,
    INVENTORY,
    ;

    fun requiredPermission(): AdminSettingsPermissionModule = when (this) {
        CASH_RECONCILIATION, SALES -> AdminSettingsPermissionModule.REPORTS_MONEY_VIEW
        SESSIONS, MEMBERSHIPS, ENROLLMENTS, INVENTORY -> AdminSettingsPermissionModule.REPORTS_OPS_VIEW
    }
}

fun AdminUser.allowedReports(): List<AdminReportKind> =
    AdminReportKind.entries.filter { permissions.contains(it.requiredPermission()) }

fun AdminUser.canApproveAnything(): Boolean =
    permissions.any {
        it.name.endsWith("_APPROVE")
    }

// Misma regla que el backend: solo cuentan los permisos de aprobar
fun AdminUser.canOpenApprovals(): Boolean = canApproveAnything()

fun AdminUser.isPlatform(): Boolean = role.equals("PLATFORM", ignoreCase = true)

fun AdminUser.canOpenSettings(): Boolean =
    isPlatform() ||
        permissions.any {
            it == AdminSettingsPermissionModule.CONFIG_OPS_VIEW ||
                it == AdminSettingsPermissionModule.CONFIG_OPS_EDIT ||
                it == AdminSettingsPermissionModule.CONFIG_BIZ_VIEW ||
                it == AdminSettingsPermissionModule.CONFIG_BIZ_EDIT ||
                it == AdminSettingsPermissionModule.CONFIG_SEC_VIEW ||
                it == AdminSettingsPermissionModule.CONFIG_SEC_EDIT
        } || canOpenApprovals()

fun AdminUser.hasModuleViewOrOperate(view: AdminSettingsPermissionModule, operate: AdminSettingsPermissionModule): Boolean =
    permissions.contains(view) || permissions.contains(operate)

fun AdminRoute.requiredPermission(): AdminSettingsPermissionModule? = when (this) {
    AdminRoute.Dashboard, AdminRoute.Profile, AdminRoute.Reports, AdminRoute.Settings, AdminRoute.Orders -> null
    AdminRoute.TermsMonitoring -> AdminSettingsPermissionModule.TERMS_MONITOR_VIEW
    AdminRoute.Schedules -> AdminSettingsPermissionModule.SCHEDULE_VIEW
    AdminRoute.Bookings -> AdminSettingsPermissionModule.ATTENDANCE_VIEW
    AdminRoute.BulkSignup -> AdminSettingsPermissionModule.CASH_OPERATE
    AdminRoute.Clients -> AdminSettingsPermissionModule.CLIENT_VIEW
    AdminRoute.Workers -> AdminSettingsPermissionModule.WORKER_VIEW
    AdminRoute.Services -> AdminSettingsPermissionModule.MEMBERSHIPS_VIEW
    AdminRoute.MembershipPlans -> AdminSettingsPermissionModule.MEMBERSHIPS_VIEW
    AdminRoute.MembershipOrders -> AdminSettingsPermissionModule.CASH_VIEW
    AdminRoute.Memberships -> AdminSettingsPermissionModule.MEMBERSHIPS_VIEW
    AdminRoute.ClientAccounts -> AdminSettingsPermissionModule.CASH_VIEW
    AdminRoute.Payments -> null
    AdminRoute.Marketing -> AdminSettingsPermissionModule.MARKETING_VIEW
    AdminRoute.Products -> AdminSettingsPermissionModule.CASH_VIEW
    AdminRoute.StoreSales -> AdminSettingsPermissionModule.CASH_VIEW
}

fun AdminUser.canOpenRoute(route: AdminRoute): Boolean {
    if (isPlatform()) {
        return route == AdminRoute.Settings || route == AdminRoute.Profile
    }
    return when (route) {
        AdminRoute.Dashboard, AdminRoute.Profile -> true
        AdminRoute.Payments, AdminRoute.Orders -> false
        AdminRoute.Reports -> allowedReports().isNotEmpty()
        AdminRoute.Settings -> canOpenSettings()
        AdminRoute.BulkSignup -> hasModuleViewOrOperate(
            AdminSettingsPermissionModule.CASH_VIEW,
            AdminSettingsPermissionModule.CASH_OPERATE,
        )
        AdminRoute.Clients -> hasModuleViewOrOperate(
            AdminSettingsPermissionModule.CLIENT_VIEW,
            AdminSettingsPermissionModule.CLIENT_OPERATE,
        )
        AdminRoute.Workers -> hasModuleViewOrOperate(
            AdminSettingsPermissionModule.WORKER_VIEW,
            AdminSettingsPermissionModule.WORKER_OPERATE,
        )
        AdminRoute.Services, AdminRoute.MembershipPlans, AdminRoute.Memberships -> hasModuleViewOrOperate(
            AdminSettingsPermissionModule.MEMBERSHIPS_VIEW,
            AdminSettingsPermissionModule.MEMBERSHIPS_OPERATE,
        )
        AdminRoute.MembershipOrders, AdminRoute.ClientAccounts, AdminRoute.Products, AdminRoute.StoreSales ->
            hasModuleViewOrOperate(
                AdminSettingsPermissionModule.CASH_VIEW,
                AdminSettingsPermissionModule.CASH_OPERATE,
            )
        AdminRoute.Marketing -> hasModuleViewOrOperate(
            AdminSettingsPermissionModule.MARKETING_VIEW,
            AdminSettingsPermissionModule.MARKETING_OPERATE,
        )
        else -> route.requiredPermission()?.let { permissions.contains(it) } ?: true
    }
}

data class AdminProductLicenseStatus(
    val status: String,
    val installId: String,
    val issuedAt: String? = null,
    val expiresAt: String? = null,
    val indefinite: Boolean = false,
    val graceDays: Int = 0,
    val daysRemaining: Int? = null,
    val graceDaysRemaining: Int? = null,
    val features: Set<String> = emptySet(),
    val edition: String? = null,
    val message: String? = null,
    val canActivate: Boolean = false,
) {
    val statusLabelEs: String
        get() = when (status.uppercase()) {
            "ACTIVE" -> if (indefinite) "Activa (indefinida)" else "Activa"
            "GRACE" -> "En prórroga"
            "EXPIRED" -> "Vencida"
            "NOT_ACTIVATED" -> "No activada"
            else -> status
        }
}

enum class AdminReportExportFormat(val apiValue: String, val mimeType: String, val extension: String) {
    PDF("PDF", "application/pdf", "pdf"),
    XLSX(
        "XLSX",
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        "xlsx",
    ),
}

data class AdminReportFilters(
    val branchId: String? = null,
    val trainerId: String? = null,
    val clientId: String? = null,
    val staffId: String? = null,
    val subjectType: String? = null,
    /** ACTIVE / INACTIVE (solo asistencias de socios). */
    val clientStatus: String? = null,
    /** Id del plan activo, o "NONE" para socios sin membresía (solo asistencias de socios). */
    val membership: String? = null,
    val status: String? = null,
    val method: String? = null,
    val productId: String? = null,
    val planId: String? = null,
    val categoryId: String? = null,
    val from: String? = null,
    val to: String? = null,
)

data class AdminPaymentMethodTotal(
    val method: String,
    val methodLabel: String,
    val totalCents: Long,
    val count: Int,
    val foreignUsdCents: Long? = null,
    val foreignEurCents: Long? = null,
)

data class AdminSessionReportRow(
    val date: String,
    val occurredAt: String,
    val subjectType: String,
    val subjectTypeLabel: String,
    val subjectId: String?,
    val subjectName: String,
    val source: String,
    val sourceLabel: String,
    val scheduleTitle: String,
    val trainerName: String,
)

data class AdminSessionReport(
    val sessions: Int,
    val clients: Int,
    val staff: Int,
    val accessVisits: Int,
    val currency: String,
    val rows: List<AdminSessionReportRow>,
)

data class AdminMembershipReportRow(
    val membershipId: String,
    val startsAt: String,
    val endsAt: String?,
    val branchId: String?,
    val branchName: String,
    val trainerId: String?,
    val trainerName: String,
    val clientName: String,
    val planName: String,
    val status: String,
    val statusLabel: String = status,
    val method: String = "",
    val methodLabel: String = "—",
    val priceCents: Long,
    val currency: String,
    val foreignUsdCents: Long? = null,
    val foreignEurCents: Long? = null,
    /** Cobrado en Bs de la orden (sin vuelto), también en pendientes. */
    val paidCents: Long = 0L,
    val discountCents: Long = 0L,
    val pendingBsCents: Long = 0L,
    val pendingUsdCents: Long = 0L,
    /** Descuento escrito en $ (0 si fue solo en Bs). */
    val discountUsdCents: Long = 0L,
    /** Vuelto en Bs de los cobros de la orden. */
    val changeGivenCents: Long = 0L,
)

data class AdminMembershipReport(
    val memberships: Int,
    val active: Int,
    val incomeCents: Long,
    val trainers: Int,
    val currency: String,
    val rows: List<AdminMembershipReportRow>,
    val totalsByMethod: List<AdminPaymentMethodTotal> = emptyList(),
    val discountCents: Long = 0L,
    val pendingBsCents: Long = 0L,
    val pendingUsdCents: Long = 0L,
)

data class AdminEnrollmentReportRow(
    val enrolledAt: String,
    val clientId: String?,
    val clientName: String,
    val planId: String?,
    val planName: String,
    val trainerId: String?,
    val trainerName: String,
    val status: String,
    val statusLabel: String = status,
)

data class AdminEnrollmentReport(
    val enrollments: Int,
    val clients: Int,
    val trainers: Int,
    val plans: Int,
    val rows: List<AdminEnrollmentReportRow>,
)

data class AdminCashMethodAmount(
    val method: String,
    val methodLabel: String,
    val amountCents: Long,
    val foreignUsdCents: Long? = null,
    /** Referencia de pago móvil u otra nota del método. */
    val reference: String? = null,
)

data class AdminCashReconciliationRow(
    val timeLabel: String,
    val sourceType: String,
    val sourceLabel: String,
    val clientName: String,
    val method: String,
    val methodLabel: String,
    val amountCents: Long,
    val currency: String,
    val detail: String,
    val foreignUsdCents: Long? = null,
    val foreignEurCents: Long? = null,
    val methodAmounts: List<AdminCashMethodAmount> = emptyList(),
    /** Vuelto en Bs (positivo en API); en UI se muestra negativo. */
    val changeGivenCents: Long = 0L,
    /** Día de pago (dd/MM). */
    val paidDateLabel: String? = null,
    /** Digitación (dd/MM HH:mm) solo si el día difiere del pago. */
    val registeredLabel: String? = null,
)

/** Resultado de activar el acceso del socio al portal. */
data class AdminPortalActivation(
    val sent: Boolean,
    val temporaryPassword: String?,
    val message: String,
)

data class AdminCashReconciliationReport(
    val date: String,
    val currency: String,
    val totalInCents: Long,
    val totalOutCents: Long,
    val netCents: Long,
    val rows: List<AdminCashReconciliationRow>,
    val totalsByMethod: List<AdminPaymentMethodTotal> = emptyList(),
)

data class AdminSalesReportRow(
    val saleDate: String,
    val saleNumber: Long,
    val clientId: String?,
    val customerName: String,
    val branchId: String?,
    val branchName: String,
    val trainerId: String?,
    val trainerName: String,
    val method: String,
    val methodLabel: String,
    val origin: String,
    val originLabel: String,
    val status: String,
    val statusLabel: String,
    val itemsSummary: String,
    val totalCents: Long,
    val amountPaidCents: Long = 0L,
    val changeGivenCents: Long = 0L,
    val currency: String,
    val foreignUsdCents: Long? = null,
    val foreignEurCents: Long? = null,
    val productName: String = "",
    val productId: String? = null,
    val qty: Int = 0,
    val stockQty: Int = 0,
    /** Pendiente de la venta (solo viene en su primera fila). */
    val pendingBsCents: Long = 0L,
    val pendingUsdCents: Long = 0L,
    /** Cobros de la venta, p. ej. "Pago móvil Bs 150,00 · Divisas $10,00". */
    val paymentsSummary: String = "",
    /** Descuento en Bs de la venta (solo en su primera fila). */
    val discountCents: Long = 0L,
    /** Descuento escrito en $ (solo en su primera fila). */
    val discountUsdCents: Long = 0L,
)

data class AdminInventoryReportRow(
    val productId: String?,
    val name: String,
    val categoryId: String?,
    val categoryName: String,
    val stockQty: Int,
    val minStockQty: Int,
    val stockLevel: String,
    val stockLevelLabel: String,
    val priceCents: Long,
    val priceUsdCents: Long,
    val currency: String,
    val description: String,
    val active: Boolean,
    val activeLabel: String,
)

data class AdminInventoryReport(
    val products: Int,
    val units: Int,
    val low: Int,
    val out: Int,
    val currency: String,
    val rows: List<AdminInventoryReportRow>,
)

data class AdminSalesReport(
    val sales: Int,
    val paid: Int,
    val pending: Int,
    val incomeCents: Long,
    val changeGivenCents: Long = 0L,
    val currency: String,
    val rows: List<AdminSalesReportRow>,
    val totalsByMethod: List<AdminPaymentMethodTotal> = emptyList(),
    val pendingBsCents: Long = 0L,
    val pendingUsdCents: Long = 0L,
    val discountCents: Long = 0L,
)

data class AdminReportFile(
    val fileName: String,
    val mimeType: String,
    val bytes: ByteArray,
)

enum class AdminExchangeRateStatus {
    OK,
    STALE,
    MANUAL,
    UNAVAILABLE,
    ;

    fun labelEs(): String = when (this) {
        OK -> "Actualizado"
        STALE -> "Desactualizado"
        MANUAL -> "Manual"
        UNAVAILABLE -> "Sin servicio"
    }
}

data class AdminExchangeRateQuote(
    val effectiveDate: String?,
    val eurRate: Double?,
    val usdRate: Double?,
    val cnyRate: Double?,
    val tryRate: Double?,
    val rubRate: Double?,
    val source: String?,
    val status: AdminExchangeRateStatus,
    val fetchedAt: String?,
    val manualOverrideEnabled: Boolean,
    val divisasUsesEurRate: Boolean,
)

data class AdminExchangeRateHistory(
    val effectiveDate: String,
    val eurRate: Double,
    val usdRate: Double,
    val source: String,
    val status: String,
    val fetchedAt: String?,
)

data class AdminExchangeRateSettings(
    val manualOverrideEnabled: Boolean,
    val manualEurRate: Double?,
    val manualUsdRate: Double?,
    val manualCnyRate: Double?,
    val manualTryRate: Double?,
    val manualRubRate: Double?,
    val manualEffectiveDate: String?,
    val divisasUsesEurRate: Boolean,
    val current: AdminExchangeRateQuote,
    val history: List<AdminExchangeRateHistory>,
)

data class AdminExchangeRateSettingsUpsert(
    val manualOverrideEnabled: Boolean,
    val manualEurRate: Double?,
    val manualUsdRate: Double?,
    val manualCnyRate: Double?,
    val manualTryRate: Double?,
    val manualRubRate: Double?,
    val manualEffectiveDate: String?,
    val divisasUsesEurRate: Boolean,
)

data class AdminDivisasConversion(
    val usdCents: Long,
    val eurCents: Long,
    val vesCents: Long,
    val eurRate: Double,
    val usdRate: Double,
    val effectiveDate: String?,
)

enum class AdminClientAccountEntryType {
    CHARGE,
    PAYMENT,
    CREDIT_APPLY,
    CREDIT_FROM_OVERPAY,
    ADJUSTMENT;

    fun labelEs(): String = when (this) {
        CHARGE -> "Cargo"
        PAYMENT -> "Pago"
        CREDIT_APPLY -> "Saldo aplicado"
        CREDIT_FROM_OVERPAY -> "Saldo a favor"
        ADJUSTMENT -> "Abono en monedero"
    }
}

data class AdminClientAccountSummary(
    val clientId: String,
    val clientName: String,
    val clientEmail: String?,
    val balanceCents: Long,
    val pendingDebtCents: Long,
    val currency: String,
)

data class AdminClientAccountEntry(
    val id: String,
    val entryType: AdminClientAccountEntryType,
    val amountCents: Long,
    val balanceAfterCents: Long,
    val description: String?,
    val membershipOrderId: String?,
    val membershipOrderNumber: Long?,
    val salesOrderId: String?,
    val salesOrderNumber: Long?,
    val paymentId: String?,
    val effectiveAt: String,
)

data class AdminClientAccountStatement(
    val clientId: String,
    val clientName: String,
    val balanceCents: Long,
    val pendingDebtCents: Long,
    val currency: String,
    val entries: List<AdminClientAccountEntry>,
)

data class AdminClientAccountApplyCredit(
    val membershipOrderId: String? = null,
    val salesOrderId: String? = null,
    val amountCents: Long? = null,
)

data class AdminClientAccountApplyCreditResult(
    val clientId: String,
    val appliedCents: Long,
    val balanceAfterCents: Long,
    val salesOrderId: String?,
    val membershipOrderId: String?,
)

data class AdminClientWalletDepositItem(
    val clientId: String,
    val shareCents: Long,
)

data class AdminClientWalletDepositBatch(
    val deposits: List<AdminClientWalletDepositItem>,
    val paymentTotalCents: Long,
    val description: String? = null,
)

data class AdminClientWalletDepositResult(
    val clientCount: Int,
    val paymentTotalCents: Long,
)

/** Socio en el monitoreo de términos: estado y acciones disponibles. */
data class AdminClientTermsRow(
    val id: String,
    val name: String,
    val email: String?,
    val portalAccess: Boolean,
    val phone: String?,
    val active: Boolean,
    /** ACEPTADO, PENDIENTE, RECHAZADO o SUSPENDIDO. */
    val status: String,
    val termsVersion: String?,
    val termsAcceptedAt: String?,
    val declinedVersion: String?,
    val declinedAt: String?,
    val suspensionReason: String?,
    val suspensionNote: String?,
    val suspendedAt: String?,
)

/** Canales que hoy se pueden usar para avisar: correo (SMTP) y WhatsApp (plantilla aprobada a mano). */
data class AdminTermsNoticeChannels(
    val email: Boolean,
    val whatsapp: Boolean,
    val cooldownDays: Int,
    /** Pausado por fallos seguidos: no se ofrece hasta que se reanude. */
    val emailPaused: Boolean = false,
    val whatsappPaused: Boolean = false,
)

data class AdminClientTermsMonitoring(
    val currentVersion: String,
    val available: Boolean,
    val canOperate: Boolean,
    val canApprove: Boolean,
    val channels: AdminTermsNoticeChannels,
    val members: List<AdminClientTermsRow>,
)

/** Resultado de encolar avisos: cuántos entraron a la cola y por qué se saltaron los demás. */
data class AdminTermsNoticeResult(
    val batchId: String,
    val queued: Int,
    val noContact: Int,
    val withinCooldown: Int,
    val alreadyQueued: Int,
)

/** Avance de un lote de avisos en la cola. */
data class AdminTermsNoticeProgress(
    val batchId: String,
    val total: Long,
    val sent: Long,
    val failed: Long,
    val pending: Long,
) {
    val done: Boolean get() = pending == 0L
}
